import { getAuthToken, type AuthUser } from "./auth";

export type ListMeta = {
  page: number;
  pageSize: number;
  total: number;
  sumStimmrecht?: number;
  sumMea?: number;
};
export type ListResult<T> = { data: T[]; meta: ListMeta };

export type AdminLoginInput = { username: string; password: string; rememberMe?: boolean };
export type AdminLoginResult = { token: string; user: AuthUser };
export type RolePermissionKey =
  | "dashboard"
  | "resolutions"
  | "qr_scan"
  | "resolutions_create"
  | "resolutions_update"
  | "resolutions_delete"
  | "resolutions_delete_completed"
  | "objects"
  | "objects_create"
  | "objects_update"
  | "objects_delete"
  | "units"
  | "units_create"
  | "units_update"
  | "units_delete"
  | "units_view_technical"
  | "owners"
  | "owners_create"
  | "owners_update"
  | "owners_delete"
  | "assignments"
  | "assignments_create"
  | "assignments_update"
  | "assignments_delete"
  | "imports"
  | "imports_create"
  | "imports_update"
  | "imports_delete"
  | "templates"
  | "templates_create"
  | "templates_update"
  | "templates_delete"
  | "settings"
  | "settings_create"
  | "settings_update"
  | "settings_delete"
  | "admin_users"
  | "admin_users_create"
  | "admin_users_update"
  | "admin_users_delete"
  | "platform_tenants"
  | "platform_tenants_create"
  | "platform_tenants_update"
  | "platform_tenants_delete"
  | "platform_services_notify"
  | "audit"
  | "debug";
export type RolePermissionMatrix = Record<
  "superadmin" | "platform_admin" | "platform_superadmin" | "platform_ops" | "sachbearbeitung" | "lesend",
  Record<RolePermissionKey, boolean>
>;
export type RolePermissionMatrixResponse = {
  keys: RolePermissionKey[];
  matrix: RolePermissionMatrix;
};
export type PermissionOverrideValue = "inherit" | "allow" | "deny";
export type PermissionGroupRow = {
  id: string;
  name: string;
  description?: string;
  active: boolean;
  permissions: Record<RolePermissionKey, boolean>;
};
export type UserPermissionAssignmentRow = {
  groupIds: string[];
  overrides: Record<RolePermissionKey, PermissionOverrideValue>;
};
export type PermissionGroupStateResponse = {
  keys: RolePermissionKey[];
  state: {
    groups: PermissionGroupRow[];
    userAssignments: Record<string, UserPermissionAssignmentRow>;
  };
};
export type UserObjectScopeAssignmentRow = {
  mode: "all" | "include";
  objectIds: string[];
};
export type ObjectScopeStateResponse = {
  state: {
    userAssignments: Record<string, UserObjectScopeAssignmentRow>;
  };
};
export type AdminUserRow = {
  id: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  avatarUrl?: string;
  displayName: string;
  role: "superadmin" | "platform_admin" | "platform_superadmin" | "platform_ops" | "sachbearbeitung" | "lesend";
  status: "aktiv" | "inaktiv";
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
};
export type TenantRow = {
  id: string;
  slug: string;
  name: string;
  status: "aktiv" | "inaktiv";
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
  onlineUsers?: number;
  onlineSessions?: number;
  isOnlineNow?: boolean;
  liveStatus?: {
    domain: string;
    live: boolean;
    dnsOk: boolean;
    tlsOk: boolean;
    httpOk: boolean;
    details: string;
    checkedAt: string;
  } | null;
  trial?: {
    enabled: boolean;
    startsAt?: string;
    endsAt?: string;
    expired: boolean;
    remainingDays?: number;
  } | null;
  profile?: {
    legalForm?: string;
    website?: string;
    contactEmail: string;
    contactPhone?: string;
    address?: {
      street?: string;
      postalCode?: string;
      city?: string;
      country?: string;
    };
    primaryContact?: {
      salutation?: string;
      title?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
    };
    technical: {
      timezone: string;
      locale: string;
      currency: string;
      uiFramework?: "classic" | "mockup_v2";
    };
    defaults: {
      faciliooEnabled: boolean;
      stimmrechtsart: "kopf" | "stimmrecht" | "mea";
      quorum: "allstimmig" | "einfache_mehrheit" | "qualifizierte_mehrheit";
    };
    trial?: {
      startsAt: string;
      endsAt: string;
    };
  } | null;
  primaryAdmin?: {
    id: string;
    username: string;
    displayName: string;
    status: "aktiv" | "inaktiv";
  } | null;
  webhookService?: {
    enabled: boolean;
    configured: boolean;
    statusText: "ok" | "nicht_ok" | "inaktiv" | "nicht_konfiguriert";
    queueQueued: number;
    queueProcessing: number;
    queueProcessed: number;
    lastProcessedAt?: string | null;
  };
  faciliooLiveSyncService?: {
    enabled: boolean;
    configured: boolean;
    statusText: "ok" | "nicht_ok" | "inaktiv" | "nicht_konfiguriert";
    intervalMinutes: number;
    lastFinishedAt?: string | null;
  };
};
export type TenantBillingStatus = {
  tenantId: string;
  configured: boolean;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  status: string;
  currentTier: "none" | "tier_a" | "tier_b" | "tier_c" | "variable";
  currentUnitCount: number;
  currentPriceId: string | null;
  setupFeePaidAt: string | null;
  lastCheckoutSessionId: string | null;
  lastInvoiceId: string | null;
  lastSyncedAt: string | null;
  lastSnapshot: {
    snapshotDate: string;
    activeUnitCount: number;
    resolvedTier: "tier_a" | "tier_b" | "tier_c" | "variable";
    stripePriceId: string;
    reportedUsageQuantity: number | null;
    stripeSubscriptionId: string | null;
    createdAt: string;
  } | null;
  nextSnapshotDate: string;
  activeUnitCount: number;
  resolvedTier: {
    tier: "tier_a" | "tier_b" | "tier_c" | "variable";
    priceId: string;
    reportedUsageQuantity: number | null;
  } | null;
};
export type TenantBillingCheckoutResult = {
  url: string | null;
  sessionId: string;
  activeUnitCount: number;
  resolvedTier: {
    tier: "tier_a" | "tier_b" | "tier_c" | "variable";
    priceId: string;
    reportedUsageQuantity: number | null;
  };
};
export type TenantBillingPortalResult = { url: string };
export type TenantBillingSyncResult = {
  ok: boolean;
  activeUnitCount: number;
  resolvedTier: {
    tier: "tier_a" | "tier_b" | "tier_c" | "variable";
    priceId: string;
    reportedUsageQuantity: number | null;
  };
  snapshot: NonNullable<TenantBillingStatus["lastSnapshot"]>;
};
export type TenantCreateResult = {
  tenant: TenantRow;
  admin: AdminUserRow;
  accessUrl?: string;
  provisioning?: {
    status: "skipped" | "ok" | "warning";
    domain?: string;
    message: string;
  };
};
export type TenantUpdateResult = TenantRow;
export type TenantProvisioningRetryResult = {
  tenant: TenantRow;
  provisioning: {
    status: "skipped" | "ok" | "warning";
    domain?: string;
    message: string;
  };
  seed: {
    seeded: boolean;
    reason: "no_prisma" | "demo_disabled" | "cached" | "already_seeded" | "seeded";
  };
};
export type TenantTrialRuntime = {
  enabled: boolean;
  running: boolean;
  expired: boolean;
  startsAt?: string;
  endsAt?: string;
  remainingDays?: number;
};
export type TenantFaciliooDebugState = {
  tenantSlug: string;
  debugModeEnabled: boolean;
  debugModeExpiresAt: string;
  deepProbeEnabled: boolean;
  deepProbeExpiresAt: string;
  checkedAt?: string;
  updatedAt?: string;
};
export type PlatformServiceStatusItem = {
  service: string;
  enabled: boolean;
  running: boolean;
  inProgress: boolean;
  intervalMs?: number;
  checkedAt?: string | null;
  statusText: "ok" | "nicht_ok" | "inaktiv";
  issueCount?: number;
  issues?: string[];
  hasMoreIssues?: boolean;
  lastError?: string | null;
  lastNotifyError?: string | null;
  lastNotifyAt?: string | null;
};
export type PlatformServiceStatusResponse = {
  ok: boolean;
  checkedAt: string;
  services: PlatformServiceStatusItem[];
  memoryDebug?: {
    statusText: "ok" | "warnung" | "kritisch";
    cpuCores: number;
    thresholdMb: {
      warn: number;
      critical: number;
    };
    thresholdConfig: {
      source: "dynamic" | "env_override";
      capacitySource: "system" | "cgroup" | "env_override";
      capacityMb: number;
      reservedMb: number;
      budgetMb: number;
      reserveFraction: number;
      warnFractionOfBudget: number;
      criticalFractionOfBudget: number;
      systemCapacityMb: number;
      cgroupCapacityMb: number | null;
    };
    systemMemoryMb: {
      total: number;
      free: number;
      used: number;
    };
    processShareOfSystemPercent: number;
    cpuConfig: {
      source: "system" | "cgroup" | "env_override";
      effectiveCores: number;
      systemCores: number;
      availableParallelismCores: number | null;
      cgroupQuotaCores: number | null;
      cgroupCpusetCores: number | null;
    };
    current: {
      capturedAt: string;
      rssMb: number;
      heapUsedMb: number;
      heapTotalMb: number;
      externalMb: number;
      arrayBuffersMb: number;
      cpuPercent: number;
      cpuPercentTotal: number;
    };
    history: Array<{
      capturedAt: string;
      rssMb: number;
      heapUsedMb: number;
      heapTotalMb: number;
      externalMb: number;
      arrayBuffersMb: number;
      cpuPercent: number;
      cpuPercentTotal: number;
    }>;
    uptimeSeconds: number;
  };
};
export type PlatformServiceKey =
  | "facilioo_live_sync"
  | "generated_dispatch_auto_retry"
  | "dispatch_deadline_scheduler"
  | "platform_health_monitor";
export type PlatformServiceCheckResponse = {
  ok: boolean;
  checkedAt: string;
  service: PlatformServiceStatusItem;
};
export type PlatformServiceNotifyResponse = {
  ok: boolean;
  checkedAt: string;
  service: PlatformServiceStatusItem;
  recipients: Array<{
    userId: string;
    username: string;
    displayName: string;
    email: string;
  }>;
  skipped: Array<{
    userId: string;
    username: string;
    displayName: string;
    reason: "permission_denied" | "missing_or_invalid_email";
  }>;
  sentCount: number;
  messageIds: string[];
};
export type UiBrandingApi = {
  management: {
    firmenname: string;
    email: string;
    uiFramework?: "classic" | "mockup_v2";
  };
  theme: {
    bg: string;
    surface: string;
    headerBg: string;
    line: string;
    text: string;
    primary: string;
    listHeaderBg: string;
    listAlternateRowBg: string;
    gradientEnabled: boolean;
    gradientFrom: string;
    gradientTo: string;
    navBg: string;
    navText: string;
    navActive: string;
    navActiveText: string;
    navBorder: string;
  };
  ownerTheme: {
    bg: string;
    surface: string;
    line: string;
    text: string;
    primary: string;
    gradientEnabled: boolean;
    gradientFrom: string;
    gradientTo: string;
    adminLoginBackgroundImageEnabled: boolean;
    ownerLoginBackgroundImageEnabled: boolean;
    ownerPageBackgroundImageEnabled: boolean;
  };
  logoDataUrl?: string;
  ownerLogoDataUrl?: string;
  settingsUpdatedAt?: string;
};
export type UiSettingsApi = {
  management: {
    firmenname: string;
    uiFramework: "classic" | "mockup_v2";
    faciliooEnabled: boolean;
    verwaltungsIdFacilioo: string;
    faciliooApiKey: string;
    faciliooDocumentGroupId: string;
    faciliooDocumentGroupName: string;
    faciliooProcessFeedTypeId: string;
    faciliooProcessFeedSourceId: string;
    faciliooDataConnectionMode: "manual" | "facilioo";
    faciliooStrasseTrennzeichen: string;
    faciliooMeaAttributeNames: string;
    faciliooStimmrechteAttributeNames: string;
    faciliooAnredeMapping: string;
    faciliooAnredeKeyTable: Array<{ definition: string; mapping: string; briefanrede_1?: string; briefanrede_2?: string }>;
    faciliooTitelKeywords: string;
    faciliooTitelKeyTable: Array<{ definition: string; mapping: string }>;
    faciliooOwnerImportScope: "current" | "all";
    faciliooObjectTypeFilter: string;
    demoDataEnabled: boolean;
    faciliooLiveSyncEnabled: boolean;
    faciliooLiveSyncIntervalMinutes: number;
    faciliooDebugModeEnabled: boolean;
    faciliooDebugModeExpiresAt: string;
    faciliooDeepProbeEnabled: boolean;
    faciliooDeepProbeExpiresAt: string;
    strasse: string;
    plz: string;
    ort: string;
    email: string;
    telefon: string;
  };
  mail: {
    provider: "smtp" | "brevo_api" | "microsoft_graph";
    fromName: string;
    fromEmail: string;
    replyToEmail: string;
    microsoftSharedMailboxEmail: string;
    microsoftNdrFolders: string;
    smtpHost: string;
    smtpPort: number;
    smtpSecure: boolean;
    smtpUser: string;
    smtpPassword: string;
    brevoApiKey: string;
    testRecipientEmail: string;
  };
  reminders: {
    firstReminderDays: number;
    secondReminderDays: number;
  };
  theme: {
    bg: string;
    surface: string;
    headerBg: string;
    line: string;
    text: string;
    primary: string;
    listHeaderBg: string;
    listAlternateRowBg: string;
    gradientEnabled: boolean;
    gradientFrom: string;
    gradientTo: string;
    navBg: string;
    navText: string;
    navActive: string;
    navActiveText: string;
    navBorder: string;
  };
  ownerTheme: {
    bg: string;
    surface: string;
    line: string;
    text: string;
    primary: string;
    gradientEnabled: boolean;
    gradientFrom: string;
    gradientTo: string;
    adminLoginBackgroundImageEnabled: boolean;
    ownerLoginBackgroundImageEnabled: boolean;
    ownerPageBackgroundImageEnabled: boolean;
  };
  logoDataUrl?: string;
  ownerLogoDataUrl?: string;
  settingsUpdatedAt?: string;
};

export type MailTestResult = {
  ok: boolean;
  provider: "smtp" | "brevo_api" | "microsoft_graph";
  messageId: string;
};

export type MicrosoftMailStatus = {
  ok: boolean;
  connected: boolean;
  accountEmail?: string | null;
  accountName?: string | null;
  expiresAt?: string | null;
  connectedAt?: string | null;
  updatedAt?: string | null;
};

export type MicrosoftMailFolderOption = {
  id: string;
  name: string;
  path: string;
};

export type MicrosoftMailFoldersResult = {
  ok: boolean;
  mailbox: string;
  mailboxSource: "me" | "shared_or_override";
  data: MicrosoftMailFolderOption[];
};

export type FaciliooTestResult = {
  ok: boolean;
  baseUrl: string;
  total: number;
  preview: unknown[];
};

export type FaciliooMasterDataProbeResult = {
  ok: boolean;
  mode: "count" | "full";
  baseUrl: string;
  objects: {
    endpoint: string;
    totalCount: number;
    pageNumber: number;
    totalPages: number;
    pageItems: number;
  };
  units: {
    endpoint: string;
    totalCount: number;
    pageNumber: number;
    totalPages: number;
    pageItems: number;
  };
  durationMs: number;
  full?: {
    durationMs: number;
    objectsRows: number;
    unitsRows: number;
    objectEndpointTried: string | null;
    unitEndpointTried: string | null;
    objectsTriedCount: number;
    unitsTriedCount: number;
  };
};

export type FaciliooContractLiveResult = {
  ok: boolean;
  contractId: string;
  endpoint?: string | null;
  identifier?: {
    contractId?: string | null;
    partyId?: string | null;
    unitId?: string | null;
    typeId?: string | null;
  };
  party?: {
    ok: boolean;
    partyId?: string | null;
    warning?: string | null;
    row?: Record<string, unknown>;
  };
  clear?: {
    typeLabel?: string | null;
    contract?: {
      activeByWindow?: boolean | null;
      activeByLegacy?: boolean | null;
      isCurrent?: boolean | null;
      gueltigAb?: string | null;
      gueltigBis?: string | null;
    } | null;
    faciliooUnit?: {
      objektnummer?: string | null;
      objektname?: string | null;
      einheitsnummer?: string | null;
      einheitBezeichnung?: string | null;
    } | null;
    party?: {
      fullName?: string | null;
      anrede?: string | null;
      titel?: string | null;
      vorname?: string | null;
      name1?: string | null;
      name2?: string | null;
      name3?: string | null;
      email?: string | null;
      telefon?: string | null;
      strasse?: string | null;
      plz?: string | null;
      ort?: string | null;
      land?: string | null;
      accountId?: string | null;
    } | null;
    local?: {
      objektnummer?: string | null;
      objektname?: string | null;
      einheitsnummer?: string | null;
      einheitBezeichnung?: string | null;
      eigentuemerId?: string | null;
      eigentuemerName?: string | null;
      contractId?: string | null;
      contractTypeId?: number | null;
      isAuthorizedOwnerRecipient?: boolean | null;
      rolle?: string | null;
      zuordnungStatus?: string | null;
      zuordnungAktiv?: boolean | null;
      zuordnungGueltigAb?: string | null;
      zuordnungGueltigBis?: string | null;
      matchSource?: string | null;
      hinweis?: string | null;
    } | null;
  };
  payload?: unknown;
};

export type FaciliooSyncResult = {
  startedAt?: string;
  finishedAt?: string;
  durationMs?: number;
  ok: boolean;
  message: string;
  created: number;
  updated: number;
  unchanged: number;
  fetched: number;
  datasets: {
    objects: FaciliooDatasetStats;
    units: FaciliooDatasetStats;
    owners: FaciliooDatasetStats;
    assignments: FaciliooDatasetStats;
  };
};

export type FaciliooDatasetStats = {
  fetched: number;
  created: number;
  updated: number;
  unchanged: number;
};

export type FaciliooSyncStatus = {
  running: boolean;
  cancelRequested?: boolean;
  cancelRequestedAt?: string | null;
  enabled: boolean;
  intervalMinutes: number;
  nextRunAt?: string | null;
  lastRun: FaciliooSyncResult;
  lastSuccessfulRun?: {
    finishedAt?: string;
    message?: string;
  } | null;
  currentRun?: {
    startedAt: string;
    phase:
      | "auth"
      | "objects_fetch"
      | "objects_upsert"
      | "units_fetch"
      | "units_upsert"
      | "assignments_fetch"
      | "assignments_upsert"
      | "owners_fetch"
      | "owners_upsert"
      | "cleanup"
      | "cancel_requested"
      | "cancelled"
      | "done"
      | "error";
    message: string;
    objects: { processed: number; total: number };
    units: { processed: number; total: number };
    assignments?: { processed: number; total: number };
    owners?: { processed: number; total: number };
  } | null;
  webhookRun?: {
    running: boolean;
    type: "webhook";
    datasetKeys: Array<"objects" | "units" | "owners" | "assignments">;
    startedAt?: string;
    finishedAt?: string;
    durationMs?: number;
    message?: string;
    entityNames?: string[];
    partyIds?: string[];
    processed?: number;
    total?: number;
    updatedOwners?: number;
    unchangedOwners?: number;
  } | null;
};

export type FaciliooMirrorCleanupResult = {
  ok: boolean;
  message: string;
  scanned: number;
  candidates: number;
  deleted: number;
  dryRun: boolean;
  sample: Array<{
    objektnummer: string;
    einheitsnummer: string;
    bezeichnung: string;
  }>;
};

export type FaciliooWebhookTargetStatus = {
  ok: boolean;
  authorizationType: 4;
  targetUrl: string | null;
  endpointPath: string;
  hasCredential: boolean;
  credential: {
    name: string;
    keyPrefix: string | null;
    isActive: boolean;
    lastUsedAt: string | null;
    updatedAt: string;
  } | null;
  webhookService?: {
    enabled: boolean;
    queueQueued: number;
    queueProcessing: number;
    queueProcessed: number;
    lastProcessedAt?: string | null;
  };
};

export type FaciliooWebhookTargetRegenerateResult = {
  ok: boolean;
  authorizationType: 4;
  targetUrl: string | null;
  endpointPath: string;
  apiKey: string;
  credential: {
    name: string;
    keyPrefix: string | null;
    isActive: boolean;
    lastUsedAt: string | null;
    updatedAt: string;
  };
};

export type FaciliooWebhookTargetRegisterResult = {
  ok: boolean;
  targetUrl: string;
  endpointPath: string;
  authorizationType: 4;
  apiKey: string;
  registration: {
    id: string;
    statusCode: number;
    partyId: string;
    targetUrl: string;
  };
  replaced: {
    attempted: boolean;
    previousMatches: number;
    deletedIds: string[];
  };
  credential: {
    name: string;
    keyPrefix: string | null;
    isActive: boolean;
    lastUsedAt: string | null;
    updatedAt: string;
  };
  enabledEvents: Array<{ type: number; entityName: string }>;
};

export type FaciliooWebhookConnectionTestResult = {
  ok: boolean;
  checkedAt: string;
  targetUrl: string | null;
  checks: {
    outbound: {
      ok: boolean;
      message: string;
      baseUrl: string;
      registrationFound: boolean;
      registrationId: string;
      registrationCountForTarget: number;
      registrationIds: string[];
      registrationValidation: {
        expectedEvents: Array<{ type: number; entityName: string }>;
        registeredEvents: Array<{ type: number; entityName: string }>;
        missingEvents: Array<{ type: number; entityName: string }>;
        extraEvents: Array<{ type: number; entityName: string }>;
        hasAllExpectedEvents: boolean;
      };
      deepCheck: {
        ok: boolean;
        message: string;
        webhooksLoaded: boolean;
        attemptsLoaded: boolean;
        matchingWebhooksCount: number;
        matchingAttemptsCount: number;
        latestWebhook: null | {
          webhookId: string | null;
          eventId: string | null;
          entityName: string | null;
          entityId: string | null;
          eventType: number | null;
          state: string | null;
          createdAt: string | null;
        };
        latestAttempt: null | {
          webhookId: string | null;
          tryCount: number | null;
          responseStatusCode: number | null;
          scheduledAt: string | null;
          createdAt: string | null;
        };
      };
    };
    inbound: {
      ok: boolean;
      message: string;
      lastQueuedAt: string | null;
      lastQueuedAction: string | null;
      lastEventId: string | null;
      lastQueuedWebhookId: string | null;
      lastSuccessfulInboundAt: string | null;
      lastSuccessfulInboundStatus: number | null;
      lastSuccessfulEventId: string | null;
      lastSuccessfulWebhookId: string | null;
      lastDeduplicatedAt: string | null;
      lastDeduplicatedEventId: string | null;
      lastDeduplicatedWebhookId: string | null;
      lastRejectAt: string | null;
      lastRejectStatus: number | null;
      lastRejectWebhookId: string | null;
      lastRejectReason: string | null;
      lastRejectMessage: string | null;
    };
    sync: {
      ok: boolean;
      message: string;
      liveSyncEnabled: boolean;
      intervalMinutes: number;
      lastSuccessfulAt: string | null;
    };
  };
};

export type FaciliooWebhookPingResult = {
  ok: boolean;
  targetUrl: string;
  endpointPath: string;
  statusCode: number;
  message: string;
  eventId: string;
  webhookId: string;
  apiKeyPrefix: string | null;
  responseBody: string;
};

export type DataInventoryType = "objects" | "units" | "owners" | "assignments" | "resolutions" | "all";

export type DataClearResult = {
  ok: boolean;
  message: string;
  type: DataInventoryType;
  deleted: {
    objects: number;
    units: number;
    owners: number;
    assignments: number;
    resolutions: number;
    votes: number;
    ownerVoteAccesses: number;
    dispatchEvents: number;
  };
};

type Query = {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  recipientType?: "owner" | "authorized";
  digitalOnly?: string;
  trigger?: string;
  ergebnis?: string;
  aufgang?: string;
  aufgangExact?: boolean;
  einheitstyp?: string;
  erreichbarkeit?: string;
  voteValue?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  objectId?: string;
  resolutionId?: string;
  lightweight?: string;
  limit?: number;
  cursor?: string;
};

export type ObjectRow = {
  id: string;
  objektnummer: string;
  objektname: string;
  strasse: string;
  strasse2?: string;
  plz: string;
  ort: string;
  standard_stimmrechtsart?: "kopf" | "stimmrecht" | "mea";
  anzahl_einheiten: number;
  anzahl_eigentuemer: number;
  anzahl_nicht_digital_erreichbar: number;
  stimmrecht_summe: number;
  mea_summe: number;
  status: "aktiv" | "inaktiv";
  letzte_aenderung: string;
};

export type UnitRow = {
  id: string;
  objektnummer: string;
  einheitsnummer: string;
  bezeichnung: string;
  aufgangRef?: string;
  aufgangName?: string;
  einheitstyp: string;
  stimmrecht: number;
  mea?: number;
  anzahl_eigentuemer: number;
  anzahl_nicht_digital_erreichbar: number;
  status: "aktiv" | "inaktiv";
  letzte_aenderung: string;
};

export type OwnerRow = {
  id: string;
  objectId: string;
  objektnummer: string;
  objectRefs?: Array<{
    id: string;
    objektnummer: string;
    status?: "aktiv" | "inaktiv" | "keine_zuordnung";
    assignmentDetails?: Array<{
      objektnummer: string;
      einheitsnummer: string;
      gueltigAb?: string;
      gueltigBis?: string;
      status?: "aktiv" | "inaktiv";
    }>;
  }>;
  eigentuemer_id: string;
  anrede?: string;
  titel?: string;
  vorname?: string;
  name_1?: string;
  name_2?: string;
  name_3?: string;
  briefanrede_1?: string;
  briefanrede_2?: string;
  strasse?: string;
  plz?: string;
  ort?: string;
  land_code?: string;
  land?: string;
  vollname: string;
  email?: string;
  telefon?: string;
  ist_analog: boolean;
  erreichbarkeit: "digital" | "analog";
  anzahl_einheiten: number;
  gueltig_ab?: string;
  gueltig_bis?: string;
  status: "aktiv" | "inaktiv" | "keine_zuordnung";
  letzte_aenderung: string;
};

export type AssignmentRow = {
  id: string;
  objectId: string;
  unitId: string;
  ownerId: string;
  objektnummer: string;
  einheitsnummer: string;
  eigentuemer_id: string;
  eigentuemername: string;
  eigentuemer_email?: string;
  einheit_stimmrecht: number;
  einheit_mea?: number;
  gueltig_ab?: string;
  gueltig_bis?: string;
  contractId?: string;
  contractTypeId?: number | null;
  isAuthorizedOwnerRecipient?: boolean;
  rolle?: string | null;
  letzte_aenderung?: string;
  status: "aktiv" | "inaktiv";
};

export type ResolutionRow = {
  id: string;
  objectId: string;
  createdAt: string;
  updatedAt: string;
  beschlussnummer: string;
  objektnummer: string;
  objektname: string;
  titel: string;
  status: "entwurf" | "aktiv" | "abgeschlossen";
  startdatum?: string;
  endedatum?: string;
  beschlusstext: string;
  facilioo_vorgangsnummer?: number;
  stimmrechtsart: "kopf" | "stimmrecht" | "mea";
  quorum: "einfache_mehrheit" | "qualifizierte_mehrheit" | "allstimmig";
  vorbeschluss_top?: string;
  vorbeschluss_wev_datum?: string;
  stimmberechtigte_einheiten: string[];
  owner_einzelstimmabgabe_sichtbar: boolean;
  dispatch_automation_enabled: boolean;
  trigger_notifications_enabled: boolean;
  trigger_notifications_channel: "email" | "facilioo";
  trigger_notifications_events?: Record<string, boolean>;
  quorum_erfuellt?: boolean;
  ja_stimmgewicht?: number;
  abgegebenes_stimmgewicht?: number;
  gesamt_stimmgewicht?: number;
  ruecklaufquote: number;
  abgegebene_stimmen: number;
  anzahl_analog_empfaenger?: number;
  anzahl_digital_empfaenger?: number;
  anzahl_empfaenger_gesamt?: number;
  ergebnis: string;
};

export type ObjectSummary = {
  object: {
    id: string;
    objektnummer: string;
    objektname: string;
    strasse: string;
    strasse2?: string;
    plz: string;
    ort: string;
    standard_stimmrechtsart?: "kopf" | "stimmrecht" | "mea";
    status: "aktiv" | "inaktiv";
    createdAt: string;
    updatedAt: string;
  };
  stats: {
    units: number;
    owners: number;
    assignments: number;
    owners_analog?: number;
    owners_digital?: number;
  };
};

export type ImportJobRow = {
  id: string;
  import_typ: "objects" | "units" | "owners" | "assignments";
  filename: string;
  mime_type?: string;
  status: "uploaded" | "validated" | "failed" | "imported";
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
  mapping_json?: Record<string, string>;
  error_report_json?: ImportErrorRow[];
  createdAt: string;
  updatedAt?: string;
};

export type FaciliooUnitImportIssue = {
  rowNumber: number;
  reason: "missing_reference" | "object_not_found";
  unitNumber: string;
  objectRef?: string;
};

export type FaciliooUnitImportReport = {
  total: number;
  mapped: number;
  skipped: number;
  missingReference: number;
  objectNotFound: number;
  issues: FaciliooUnitImportIssue[];
};

export type FaciliooUploadResult = ImportJobRow & {
  source?: "facilioo";
  sourceRows?: number;
  unitReport?: FaciliooUnitImportReport;
};

export type ImportErrorRow = {
  id: string;
  rowNumber: number;
  field: string;
  code: string;
  message: string;
  severity?: "error" | "warning";
  overridable?: boolean;
};

export type ImportValidateResult = {
  jobId: string;
  status: ImportJobRow["status"];
  summary: {
    total_rows: number;
    valid_rows: number;
    invalid_rows: number;
  };
  errors: ImportErrorRow[];
};

export type AuditRow = {
  id: string;
  actor_id: string;
  actor_name?: string;
  action: string;
  entity_type: string;
  entity_id: string;
  object_ref?: string;
  changes_json?: unknown;
  createdAt: string;
};

export type FaciliooWebhookQueueRow = AuditRow & {
  db_processed?: boolean | null;
  db_state?: "queued" | "processing" | "processed" | "missing";
  db_processed_at?: string | null;
  db_error_message?: string | null;
};

export type FaciliooWebhookRejectRow = {
  id: string;
  createdAt: string;
  status: number;
  reason: string;
  message: string;
  eventId?: string;
  webhookId?: string;
  entityName?: string;
  entityId?: string;
  eventType?: number;
  requestId?: string;
  apiKeyPrefix?: string;
  ip?: string;
  userAgent?: string;
};

export type VoteProtocolRow = {
  id: string;
  timestamp: string;
  resolution_id?: string;
  beschlussnummer?: string;
  objektnummer?: string;
  objektname?: string;
  einheit_refs?: string;
  owner_id?: string;
  eigentuemer_id?: string;
  owner_anrede?: string;
  owner_vorname?: string;
  owner_name_1?: string;
  owner_name_2?: string;
  owner_name_3?: string;
  owner_fullname?: string;
  vote_value?: "ja" | "nein" | "enthaltung" | "offen" | string;
  vote_event_type?: string;
  submission_channel?: string;
  auth_mode?: string;
  auth_user_name?: string;
  client_ip?: string;
  comment?: string;
  contract_ids?: string;
};

type VoteProtocolsQuery = Query & {
  voteValue?: string;
  objectRef?: string;
  unit?: string;
  owner?: string;
  submissionChannel?: string;
};

export type DashboardData = {
  offeneBeschluesse: number;
  ueberfaellig: number;
  fehlerhafteImporte: number;
  unvollstaendigeObjekte: number;
  recent: Array<{ id: string; action: string; entity_type: string; entity_id: string; createdAt: string }>;
};

export type DashboardSummary = {
  generatedAt: string;
  reminders: {
    secondReminderDays: number;
  };
  kpis: {
    currentResolutions: number;
    activeResolutions: number;
    draftResolutions: number;
    totalReturnRate: number;
    criticalDeadlines: number;
    dataQualityIssues: number;
  };
  resolutionStatus: Record<"entwurf" | "aktiv" | "abgeschlossen", number>;
  voteSummary: Record<"ja" | "nein" | "enthaltung", number>;
  historicalMetrics: {
    averageReturnRate: number;
    voteDistribution: Record<"ja" | "nein" | "enthaltung", number>;
    completedOutcomes: Record<"angenommen" | "abgelehnt", number>;
    quorumDistribution: Record<"erreicht" | "nichtErreicht", number>;
  };
  dataQuality: Array<{
    key: string;
    label: string;
    count: number;
    tone: "ok" | "warning" | "critical";
    href: string;
  }>;
  currentResolutions: Array<{
    id: string;
    beschlussnummer: string;
    objectId: string;
    objektnummer: string;
    objektname: string;
    titel: string;
    status: "entwurf" | "aktiv" | "abgeschlossen";
    startdatum?: string;
    endedatum?: string;
    ruecklaufquote: number;
    abgegebene_stimmen: number;
    anzahl_empfaenger_gesamt: number;
    quorum_erfuellt: boolean;
    ergebnis: string;
    daysUntilEnd: number | null;
    isCriticalAfterSecondReminder: boolean;
  }>;
};

export type AssignmentAnomalyUnit = {
  id: string;
  objectId: string;
  objektnummer: string;
  einheitsnummer: string;
  bezeichnung: string;
  einheitstyp: string;
  status: "aktiv" | "inaktiv";
  activeAssignmentCount: number;
  relevantForOwnerAssignmentDiagnostics: boolean;
};

export type AssignmentAnomaliesResult = {
  generatedAt: string;
  totalActiveUnits: number;
  relevantActiveUnits: number;
  ignoredActiveUnits: number;
  unitsWithoutActiveAssignment: AssignmentAnomalyUnit[];
  unitsWithMultipleActiveAssignments: AssignmentAnomalyUnit[];
  ignoredUnitsWithoutActiveAssignment: AssignmentAnomalyUnit[];
};

export type CreateObjectInput = {
  objektnummer: string;
  objektname: string;
  strasse: string;
  strasse2?: string;
  plz: string;
  ort: string;
  standard_stimmrechtsart?: "kopf" | "stimmrecht" | "mea";
  status: "aktiv" | "inaktiv";
};

export type CreateUnitInput = {
  objectId: string;
  einheitsnummer: string;
  bezeichnung: string;
  einheitstyp: string;
  stimmrecht: number;
  mea?: number;
  status: "aktiv" | "inaktiv";
};

export type CreateOwnerInput = {
  objectId?: string;
  eigentuemer_id: string;
  anrede?: string;
  titel?: string;
  vorname?: string;
  name_1?: string;
  name_2?: string;
  name_3?: string;
  strasse?: string;
  plz?: string;
  ort?: string;
  land_code?: string;
  land?: string;
  email?: string;
  telefon?: string;
  status: "aktiv" | "inaktiv";
};

export type CreateAssignmentInput = {
  objectId: string;
  unitId: string;
  ownerId: string;
  status: "aktiv" | "inaktiv";
  gueltig_ab?: string;
  gueltig_bis?: string;
  uebernahme_von_assignment_id?: string;
  vorgaenger_gueltig_bis?: string;
};

export type CreateResolutionInput = {
  beschlussnummer?: string;
  objectId: string;
  titel: string;
  startdatum?: string;
  endedatum?: string;
  beschlusstext: string;
  facilioo_vorgangsnummer?: number;
  owner_einzelstimmabgabe_sichtbar?: boolean;
  dispatch_automation_enabled?: boolean;
  trigger_notifications_enabled?: boolean;
  trigger_notifications_channel?: "email" | "facilioo";
  trigger_notifications_events?: Record<string, boolean>;
  stimmrechtsart?: "kopf" | "stimmrecht" | "mea";
  quorum?: "einfache_mehrheit" | "qualifizierte_mehrheit" | "allstimmig";
  vorbeschluss_top?: string;
  vorbeschluss_wev_datum?: string;
  stimmberechtigte_einheiten?: string[];
  auswertungsart?: "einheit" | "kopf";
};


export type ResolutionVoteRow = {
  id: string;
  resolutionId: string;
  assignmentId: string;
  voteValue: "ja" | "nein" | "enthaltung" | "offen";
  voteReceivedAt?: string;
  comment?: string;
  createdAt: string;
  updatedAt: string;
};

export type ResolutionRecipientRow = {
  assignmentId: string;
  einheitsnummer: string;
  lage: string;
  einheitstyp: string;
  eigentuemerId: string;
  eigentuemerName: string;
  email?: string;
  erreichbarkeit: "digital" | "analog";
  stimmrecht: number;
  mea?: number;
  voteValue: "ja" | "nein" | "enthaltung" | "offen";
  voteReceivedAt?: string;
  comment?: string;
};

export type ResolutionVoteLogRow = {
  timestamp?: string;
  vote_event_type?: string;
  client_ip?: string;
  submission_channel?: string;
  auth_mode?: string;
  auth_user_id?: string;
  auth_user_name?: string;
  owner_access_ref?: string;
  resolution_id?: string;
  beschlussnummer?: string;
  objektnummer?: string;
  actor_id?: string;
  actor_name?: string;
  owner_id?: string;
  owner_assignment_ids?: string[];
  vote_value?: "ja" | "nein" | "enthaltung" | "offen";
  comment?: string;
  changed_assignments?: Array<{
    assignmentId?: string;
    previousVoteValue?: string;
    nextVoteValue?: string;
  }>;
};


export type ResolutionDispatchLogRow = {
  id: string;
  resolutionId: string;
  beschlussnummer: string;
  objektnummer: string;
  eventType: "einladung_versendet" | "erinnerung_1_versendet" | "erinnerung_2_versendet" | "beschlussergebnis_versendet";
  timestamp: string;
  total_empfaenger: number;
  offene_rueckmeldungen: number;
  actor_id: string;
  actor_name: string;
};


export type DemoDispatchEventType = "einladung_versendet" | "erinnerung_1_versendet" | "erinnerung_2_versendet" | "beschlussergebnis_versendet";

export type DemoDispatchMessageRow = {
  id: string;
  ownerId: string;
  eigentuemer_id: string;
  eigentuemername: string;
  email?: string;
  status: "queued" | "sent" | "failed" | "skipped";
  attempts: number;
  maxAttempts: number;
  lastError?: string;
  lastUpdateAt: string;
  sentAt?: string;
};

export type DemoDispatchJobSummary = {
  id: string;
  resolutionId: string;
  beschlussnummer: string;
  objektnummer: string;
  eventType: DemoDispatchEventType;
  status: "running" | "completed" | "completed_with_errors" | "cancelled";
  createdAt: string;
  startedAt: string;
  finishedAt?: string;
  options: {
    maxPerSecond: number;
    failRatePercent: number;
  };
  stats: {
    total: number;
    digital: number;
    analog: number;
    queued: number;
    sent: number;
    failed: number;
    skipped: number;
  };
};

export type DemoDispatchStatusResponse = {
  jobs: DemoDispatchJobSummary[];
  latestJob: (DemoDispatchJobSummary & { messages: DemoDispatchMessageRow[] }) | null;
};
export type ResolutionDocumentRow = {
  id: string;
  resolutionId: string;
  originalName: string;
  storedName: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
  uploadedBy: string;
  uploadedByName: string;
  fileUrl: string;
};

export type ResolutionFaciliooAttachmentRow = {
  feedId: string;
  attachmentId: string;
  originalFilename: string;
  internalFilename: string;
  filesizeKb: number | null;
  createdAt: string | null;
  lastModifiedAt: string | null;
  typeId: number | null;
  fileExtension: string | null;
  mimeType: string | null;
  fileTypeLabel: string | null;
};

export type ResolutionFaciliooFileTypeDebugRow = {
  id: string;
  fileExtension: string;
  description: string;
  mimeType: string;
};

export type ResolutionFaciliooUploadDocumentsResult = {
  uploaded: Array<{ documentId: string; originalName: string; fileId: string; feedId: string }>;
  uploadedCount: number;
  skipped: Array<{ documentId: string; originalName?: string; reason: string }>;
  skippedCount: number;
  processId: string;
  processFeedId: string;
  debug?: {
    createdFeedId?: string;
    items?: Array<{
      documentId: string;
      originalName?: string;
      fileId?: string;
      feedId: string;
      attachStatus: "attached" | "not_attached";
      attachmentCountAfterAttach: number | null;
      attachAttempts: Array<{ endpoint: string; status: number; ok: boolean }>;
    }>;
  };
};
export type DocumentTemplateKey =
  | "invitation_digital"
  | "invitation_analog"
  | "reminder1_digital"
  | "reminder1_analog"
  | "reminder2_digital"
  | "reminder2_analog"
  | "result_digital"
  | "result_analog"
  | "ballot_analog"
  | "trigger_resolution_started"
  | "trigger_invitation_sent_success"
  | "trigger_email_bounce_after_retries"
  | "trigger_analog_postal_action_required"
  | "trigger_vote_with_comment"
  | "trigger_reminder_sent"
  | "trigger_each_vote_submitted"
  | "trigger_preliminary_result_locked"
  | "trigger_resolution_finished_with_result";

export type DocumentTemplateEventType =
  | "einladung_versendet"
  | "erinnerung_1_versendet"
  | "erinnerung_2_versendet"
  | "beschlussergebnis_versendet"
  | "resolution_started"
  | "invitation_sent_success"
  | "email_bounce_after_retries"
  | "analog_postal_action_required"
  | "vote_with_comment"
  | "reminder_sent"
  | "each_vote_submitted"
  | "preliminary_result_locked"
  | "resolution_finished_with_result";

export type DocumentTemplateRow = {
  key: DocumentTemplateKey;
  name: string;
  kanal: "digital" | "analog";
  eventType: DocumentTemplateEventType;
  subject: string;
  body: string;
  updatedAt: string;
};

export type FaciliooEnsureDocumentGroupResult = {
  ok: boolean;
  created: boolean;
  reused: boolean;
  groupId: string;
  groupName: string;
};

export type FaciliooEnsureDocumentGroupPayload = {
  groupName?: string;
  forceCreate?: boolean;
  parentGroupId?: string;
};

export type FaciliooResetDocumentGroupResult = {
  ok: boolean;
  groupId: string;
  groupName: string;
};

export type FaciliooDocumentGroupOption = {
  groupId: string;
  groupName: string;
};

export type FaciliooDocumentGroupsResult = {
  ok: boolean;
  data: FaciliooDocumentGroupOption[];
};

export type DocumentTemplatesResponse = {
  templates: DocumentTemplateRow[];
  placeholders: string[];
  placeholderGroups?: Array<{ key: string; label: string; tokens: string[] }>;
  letterheads?: {
    digital?: {
      fileName?: string;
      updatedAt?: string;
      fileUrl?: string;
      topMarginMm: number;
      bottomMarginMm: number;
      firstPageRecipientTopMm?: number;
      firstPageBodyStartMm?: number;
    };
    analog?: {
      fileName?: string;
      updatedAt?: string;
      fileUrl?: string;
      topMarginMm: number;
      bottomMarginMm: number;
      firstPageRecipientTopMm?: number;
      firstPageBodyStartMm?: number;
    };
    digital_followup?: { fileName?: string; updatedAt?: string; fileUrl?: string; topMarginMm: number; bottomMarginMm: number };
    analog_followup?: { fileName?: string; updatedAt?: string; fileUrl?: string; topMarginMm: number; bottomMarginMm: number };
  };
};

export type DocumentTemplatePreviewResponse = {
  contextOwner: {
    id: string;
    eigentuemer_id: string;
    name: string;
  };
  subject: string;
  body: string;
};

export type ResolutionTriggerNotificationLogRow = {
  id: string;
  resolutionId: string;
  eventType: string;
  channel: "email" | "facilioo";
  status: "queued" | "processing" | "sent" | "failed" | "skipped";
  attempts: number;
  maxAttempts: number;
  createdAt: string;
  updatedAt: string;
  nextAttemptAt?: string;
  lastError?: string;
  messageId?: string;
  recipientEmail?: string;
  deliveryEndpoint?: string;
  deliveryHttpStatus?: number;
  deliveryRequestBody?: string;
  deliveryResponseBody?: string;
  deliveredAt?: string;
};

export type DocumentTemplateMailPreviewResponse = {
  templateKey: DocumentTemplateKey;
  eventType: DocumentTemplateEventType;
  contextMode: "sample" | "resolution";
  contextOwner: {
    id: string;
    eigentuemer_id: string;
    name: string;
  } | null;
  subject: string;
  body: string;
  html: string;
};

export type DocumentTemplateMailSendTestResponse = {
  ok: boolean;
  messageId?: string;
  recipientEmail: string;
};

export type GeneratedDispatchRow = {
  id: string;
  batchId: string;
  resolutionId: string;
  beschlussnummer: string;
  objectId: string;
  objektnummer: string;
  ownerId: string;
  eigentuemerId: string;
  eigentuemerName: string;
  kanal: "digital" | "analog";
  eventType: "einladung_versendet" | "erinnerung_1_versendet" | "erinnerung_2_versendet" | "beschlussergebnis_versendet";
  templateKey: DocumentTemplateKey;
  subject: string;
  body: string;
  fileName: string;
  relativePath: string;
  createdAt: string;
  digitalMailTo?: string;
  digitalMailStatus?: "sent" | "failed" | "skipped";
  digitalMailMessageId?: string;
  digitalMailError?: string;
  digitalMailAttemptedAt?: string;
  digitalMailSentAt?: string;
  digitalMailRetries?: number;
  digitalMailNextRetryAt?: string;
  digitalMailNdrDetectedAt?: string;
  digitalMailNdrMessageId?: string;
  digitalMailNdrFailureClass?: "temporary" | "permanent" | "unknown";
  digitalMailNdrEnhancedStatusCode?: string;
  digitalMailNdrSmtpStatusCode?: string;
  digitalMailNdrReason?: string;
  temporaryAnalogRequiredAt?: string;
  temporaryAnalogReason?: string;
  temporaryAnalogConvertedAt?: string;
  temporaryAnalogBatchId?: string;
  temporaryAnalogTriggerQueuedAt?: string;
  temporaryAnalogLastError?: string;
  fileUrl: string;
};

export type RetryGeneratedMailResult = {
  id: string;
  digitalMailStatus?: "sent" | "failed" | "skipped";
  digitalMailMessageId?: string;
  digitalMailError?: string;
  digitalMailTo?: string;
  digitalMailAttemptedAt?: string;
  digitalMailSentAt?: string;
  digitalMailRetries?: number;
  digitalMailNextRetryAt?: string;
  replacedDigitalMailTo?: string;
  usedCurrentOwnerEmail?: boolean;
};

export type RetryFailedGeneratedMailsResult = {
  attempted: number;
  sent: number;
  failed: number;
  skipped: number;
  maxAttempts: number;
  baseDelayMs: number;
  maxDelayMs: number;
  overview: {
    totalDigital: number;
    sent: number;
    failed: number;
    skipped: number;
    pendingRetry: number;
    exhausted: number;
    nextRetryAt: string | null;
  };
};

export type FaciliooProcessResolveResult = {
  ok: boolean;
  baseUrl: string;
  input: string;
  expectedQualifiedRef?: string;
  resolved: {
    processId: string | null;
    processNumber: string | null;
    processQualifiedNumber?: string | null;
    title: string | null;
    rowSummary: string;
    strategy: string;
    endpoint: string;
  };
  attempts: Array<{
    strategy: string;
    url: string;
    status: number;
    ok: boolean;
    note?: string;
    requestBody?: string;
  }>;
  scannedRows?: number;
  feed?: {
    requested: boolean;
    attempted: boolean;
    ok: boolean;
    endpoint: string;
    status?: number;
    requestBody?: string;
    note?: string;
  };
};

export type FaciliooProcessValidationResult = {
  ok: boolean;
  processId?: string | null;
  processNumber?: string | null;
  processQualifiedNumber?: string | null;
  processTitle?: string | null;
  objectMatchReason?: string;
};

export type NextResolutionNumberResponse = {
  beschlussnummer: string;
};

export type DispatchGenerateResult = {
  batchId: string;
  eventType: string;
  createdAt: string;
  count: number;
  digitalMail?: {
    sent: number;
    failed: number;
    total: number;
  };
  digitalMailErrors?: Array<{
    ownerId: string;
    email: string;
    templateKey: DocumentTemplateKey;
    error: string;
  }>;
  data: GeneratedDispatchRow[];
};
export type OwnerVoteAccessRow = {
  id: string;
  ownerId: string;
  eigentuemer_id: string;
  eigentuemername: string;
  email?: string;
  status: "aktiv" | "verwendet" | "gesperrt";
  einmalpasswort: string;
  zugangspasswort?: string;
  token: string;
  direct_path: string;
  beschlussnummer: string;
  aktuelle_stimme: "ja" | "nein" | "enthaltung" | "offen";
  zuletzt_abgegeben_am?: string;
};

export type OwnerVotingLoginInput = {
  beschluss_id: string;
  zugangspasswort: string;
  einmalpasswort?: string;
};

export type OwnerVotingLoginResult = {
  access_token: string;
  redirect_path: string;
  resolution_id: string;
  beschlussnummer: string;
};

export type OwnerVotingAccessData = {
  access: {
    token: string;
    status: "aktiv" | "verwendet" | "gesperrt";
    can_vote: boolean;
    usedAt?: string;
    lastLoginAt?: string;
  };
  resolution: ResolutionRow;
  owner: {
    id: string;
    eigentuemer_id: string;
    name: string;
    email?: string;
  };
  assignments: Array<{
    id: string;
    unitId: string;
    einheitsnummer: string;
    einheitstyp?: string;
    stimmrecht?: number;
    mea?: number;
  }>;
  current_vote: null | {
    voteValue: "ja" | "nein" | "enthaltung" | "offen";
    comment?: string;
    voteReceivedAt?: string;
  };
};

export type OwnerVotingSubmitInput = {
  voteValue: "ja" | "nein" | "enthaltung";
  comment?: string;
  auth_mode?: "beschluss_link" | "beschluss_id_passwort";
};

export type AnalogScanAccessData = {
  token: string;
  resolution: ResolutionRow;
  owner: {
    id: string;
    eigentuemer_id: string;
    name: string;
  };
  assignments: Array<{ id: string; unitId: string; einheitsnummer: string }>;
  current_vote: null | {
    voteValue: "ja" | "nein" | "enthaltung" | "offen";
    comment?: string;
    voteReceivedAt?: string;
  };
};

export type AnalogScanSubmitInput = {
  voteValue: "ja" | "nein" | "enthaltung";
  comment?: string;
};

export type ImportType = "objects" | "units" | "owners" | "assignments";

export type ResolutionResult = {
  resolutionId: string;
  status: "entwurf" | "aktiv" | "abgeschlossen";
  stimmrechtsart: "kopf" | "stimmrecht" | "mea";
  quorum: "einfache_mehrheit" | "qualifizierte_mehrheit" | "allstimmig";
  vorbeschluss_top?: string;
  vorbeschluss_wev_datum?: string;
  quorum_hinweis?: string;
  break_even?: {
    isLocked: boolean;
    lockedOutcome?: "angenommen" | "abgelehnt";
    ruleText: string;
    explanation: string;
  };
  stimmberechtigte_einheiten: string[];
  owner_einzelstimmabgabe_sichtbar: boolean;
  quorumErfuellt: boolean;
  stimmen: {
    ja: number;
    nein: number;
    enthaltung: number;
    offen: number;
    abgegeben: number;
    gesamt: number;
  };
  ergebnis: string;
  einzelstimmabgaben: Array<{
    ownerId: string;
    ownerLabel: string;
    voteValue: "ja" | "nein" | "enthaltung" | "offen";
    voteReceivedAt?: string;
    comment?: string;
    einheiten: string[];
  }>;
};

export type ResolutionKpiResult = {
  resolutionId: string;
  status: "entwurf" | "aktiv" | "abgeschlossen";
  updatedAt: string;
  ruecklaufquote: number;
  abgegebene_stimmen: number;
  recipient: {
    digital: number;
    analog: number;
    gesamt: number;
  };
  quorumErfuellt: boolean;
  ergebnis: string;
  break_even?: {
    isLocked: boolean;
    lockedOutcome?: "angenommen" | "abgelehnt";
    ruleText: string;
    explanation: string;
  };
  stimmen: {
    ja: number;
    nein: number;
    enthaltung: number;
    offen: number;
    abgegeben: number;
    gesamt: number;
  };
};

export type CreateResolutionVoteInput = {
  assignmentId: string;
  voteValue: "ja" | "nein" | "enthaltung" | "offen";
  comment?: string;
  expectedUpdatedAt?: string;
};

export type ResolutionRealtimeSnapshot = {
  resolutionId: string;
  updatedAt: string;
  status: "entwurf" | "aktiv" | "abgeschlossen";
  dispatchLatestAt?: string;
  voteLatestAt?: string;
  triggerLatestAt?: string;
  serverTime: string;
};

const normalizeApiBase = (value: string) => value.trim().replace(/\/+$/, "");

const resolveApiBase = (): string => {
  const runtimeBase = (globalThis as { __APP_API_BASE_URL__?: string }).__APP_API_BASE_URL__;
  if (typeof runtimeBase === "string" && runtimeBase.trim()) return normalizeApiBase(runtimeBase);
  if (typeof window !== "undefined" && window.location?.origin) {
    const { hostname, port, protocol } = window.location;
    if (port === "5173" || port === "5180") {
      return `${protocol}//${hostname}:8080/api/v1`;
    }
    return `${window.location.origin}/api/v1`;
  }
  return "/api/v1";
};

const API_BASE = resolveApiBase();
const API_ORIGIN = /^https?:\/\//i.test(API_BASE) ? API_BASE.replace(/\/api\/v1\/?$/, "") : "";
const API_GET_ETAG_CACHE_LIMIT = 250;
const apiGetEtagCache = new Map<string, { etag: string; data: unknown }>();

const toApiFileUrl = (fileUrl?: string): string => {
  const raw = (fileUrl ?? "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (!API_ORIGIN) return raw.startsWith("/") ? raw : `/${raw}`;
  return `${API_ORIGIN}${raw.startsWith("/") ? "" : "/"}${raw}`;
};

const normalizeAuthUserAvatar = (user: AuthUser): AuthUser => ({
  ...user,
  avatarUrl: user.avatarUrl ? toApiFileUrl(user.avatarUrl) : undefined
});

const buildHeaders = (contentType = true): HeadersInit => {
  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (contentType) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const toSearchParams = (query: Query = {}) => {
  const p = new URLSearchParams();
  Object.entries(query).forEach(([k, v]) => {
    if (v !== undefined && v !== "") p.set(k, String(v));
  });
  return p.toString();
};

const setApiGetEtagCacheEntry = (url: string, etag: string, data: unknown) => {
  if (apiGetEtagCache.has(url)) apiGetEtagCache.delete(url);
  apiGetEtagCache.set(url, { etag, data });
  if (apiGetEtagCache.size <= API_GET_ETAG_CACHE_LIMIT) return;
  const oldestKey = apiGetEtagCache.keys().next().value;
  if (oldestKey) apiGetEtagCache.delete(oldestKey);
};

async function readErrorDetail(res: Response): Promise<string> {
  try {
    const raw = (await res.text()).trim();
    if (!raw) return "";

    const tryParseJson = (): unknown => {
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    };

    const payload = tryParseJson() as {
      error?: unknown;
      expectedQualifiedRef?: string;
      attempts?: Array<{ strategy?: string; url?: string; status?: number; requestBody?: string }>;
      debugMatchedObjectNumber?: string | null;
      debugSelectedObjectPropertyId?: string | null;
      debugProcessPropertyId?: string | null;
      debugProcessPropertyRefs?: string[];
    };

    if (payload && typeof payload === "object") {
      if (typeof payload.error === "string") return payload.error.trim();
      if (payload.error) {
        const messages = new Set<string>();
        const collectMessages = (value: unknown) => {
          if (!value) return;
          if (typeof value === "string") {
            const trimmed = value.trim();
            if (trimmed) messages.add(trimmed);
            return;
          }
          if (Array.isArray(value)) {
            value.forEach(collectMessages);
            return;
          }
          if (typeof value === "object") {
            Object.values(value as Record<string, unknown>).forEach(collectMessages);
          }
        };
        collectMessages(payload.error);
        if (messages.size > 0) return Array.from(messages).join(" ");
      }
    }

    const plain = raw
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return plain.slice(0, 500);
  } catch {
    return "";
  }
}

async function buildApiErrorMessage(res: Response): Promise<string> {
  const detail = (await readErrorDetail(res)).trim();
  return detail || "Die Anfrage konnte nicht verarbeitet werden. Bitte erneut versuchen.";
}

async function apiGet<T>(path: string, query?: Query): Promise<T> {
  const qs = query ? toSearchParams(query) : "";
  const url = `${API_BASE}${path}${qs ? `?${qs}` : ""}`;
  const headers = buildHeaders(false) as Record<string, string>;
  const cached = apiGetEtagCache.get(url);
  if (cached?.etag) headers["If-None-Match"] = cached.etag;
  const res = await fetch(url, { headers });
  if (res.status === 304 && cached) return cached.data as T;
  if (!res.ok) throw new Error(await buildApiErrorMessage(res));
  const payload = (await res.json()) as T;
  const etag = String(res.headers.get("etag") ?? "").trim();
  if (etag) setApiGetEtagCacheEntry(url, etag, payload);
  return payload;
}

async function apiPost<TResponse, TBody>(path: string, body: TBody): Promise<TResponse> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error(await buildApiErrorMessage(res));
  return (await res.json()) as TResponse;
}

async function apiPostBlob<TBody>(path: string, body: TBody): Promise<Blob> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error(await buildApiErrorMessage(res));
  return await res.blob();
}

async function apiPut<TResponse, TBody>(path: string, body: TBody): Promise<TResponse> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "PUT",
    headers: buildHeaders(),
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error(await buildApiErrorMessage(res));
  return (await res.json()) as TResponse;
}

async function apiPatch<TResponse, TBody>(path: string, body: TBody): Promise<TResponse> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "PATCH",
    headers: buildHeaders(),
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error(await buildApiErrorMessage(res));
  return (await res.json()) as TResponse;
}


async function apiPostForm<TResponse>(path: string, formData: FormData): Promise<TResponse> {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    "x-user-id": "frontend-user",
    "x-user-name": "Frontend User"
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers,
    body: formData
  });
  if (!res.ok) throw new Error(await buildApiErrorMessage(res));
  return (await res.json()) as TResponse;
}

async function apiGetBlob(path: string): Promise<Blob> {
  const res = await fetch(`${API_BASE}${path}`, { headers: buildHeaders(false) });
  if (!res.ok) throw new Error(await buildApiErrorMessage(res));
  return await res.blob();
}
async function apiDelete(path: string): Promise<void> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "DELETE",
    headers: buildHeaders()
  });
  if (!res.ok) throw new Error(await buildApiErrorMessage(res));
}

const parseSseBlock = (raw: string): { event: string; data: unknown } | null => {
  const lines = raw.split(/\r?\n/);
  let event = "message";
  const dataLines: string[] = [];
  for (const line of lines) {
    if (!line) continue;
    if (line.startsWith("event:")) {
      event = line.slice(6).trim() || "message";
      continue;
    }
    if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trimStart());
    }
  }
  if (!dataLines.length) return null;
  const rawData = dataLines.join("\n").trim();
  if (!rawData) return { event, data: null };
  try {
    return { event, data: JSON.parse(rawData) };
  } catch {
    return { event, data: rawData };
  }
};

const compactPermissionGroupState = (state: PermissionGroupStateResponse["state"]): PermissionGroupStateResponse["state"] => {
  const groups = state.groups.map((group) => {
    const enabledPermissions = Object.fromEntries(
      Object.entries(group.permissions).filter(([, value]) => value === true)
    ) as Record<RolePermissionKey, boolean>;
    return {
      ...group,
      permissions: enabledPermissions
    };
  });

  const userAssignments = Object.fromEntries(
    Object.entries(state.userAssignments)
      .map(([userId, assignment]) => {
        const groupIds = Array.isArray(assignment.groupIds) ? assignment.groupIds.filter(Boolean).slice(0, 1) : [];
        const relevantOverrides = Object.fromEntries(
          Object.entries(assignment.overrides).filter(([, value]) => value === "allow" || value === "deny")
        ) as Record<RolePermissionKey, PermissionOverrideValue>;
        if (!groupIds.length && !Object.keys(relevantOverrides).length) return null;
        return [userId, { groupIds, overrides: relevantOverrides }] as const;
      })
      .filter((entry): entry is readonly [string, UserPermissionAssignmentRow] => Boolean(entry))
  );

  return { groups, userAssignments };
};

export type DemoRequestConfig = {
  captchaSiteKey: string;
  targetEmail: string;
};

export type DemoRequestPayload = {
  fullName: string;
  company: string;
  email: string;
  phone?: string;
  managedUnits?: string;
  message: string;
  requestType: "demo_access" | "personal_demo";
  captchaToken: string;
};

export const api = {
  adminLogin: async (payload: AdminLoginInput) => {
    const res = await apiPost<AdminLoginResult, AdminLoginInput>("/auth/login", payload);
    return { ...res, user: normalizeAuthUserAvatar(res.user) };
  },
  adminMe: async () => {
    const resRaw = await fetch(`${API_BASE}/auth/me`, { headers: buildHeaders(false) });
    if (!resRaw.ok) throw new Error(`API ${resRaw.status}: ${await buildApiErrorMessage(resRaw)}`);
    const res = (await resRaw.json()) as { user: AuthUser };
    return { ...res, user: normalizeAuthUserAvatar(res.user) };
  },
  adminHeartbeat: async () => {
    const res = await fetch(`${API_BASE}/auth/heartbeat`, {
      method: "POST",
      headers: buildHeaders()
    });
    if (!res.ok) throw new Error(`API ${res.status}: ${await buildApiErrorMessage(res)}`);
  },
  adminLogout: () => apiPost<void, Record<string, never>>("/auth/logout", {}),
  adminUsers: async () => {
    const res = await apiGet<{ data: AdminUserRow[] }>("/auth/users");
    return {
      ...res,
      data: (res.data ?? []).map((row) => ({ ...row, avatarUrl: row.avatarUrl ? toApiFileUrl(row.avatarUrl) : undefined }))
    };
  },
  rolePermissionMatrix: () => apiGet<RolePermissionMatrixResponse>("/auth/permissions/role-matrix"),
  updateRolePermissionMatrix: (matrix: RolePermissionMatrix) =>
    apiPatch<RolePermissionMatrixResponse, { matrix: RolePermissionMatrix }>("/auth/permissions/role-matrix", { matrix }),
  permissionGroups: () => apiGet<PermissionGroupStateResponse>("/auth/permissions/groups"),
  updatePermissionGroups: (state: PermissionGroupStateResponse["state"]) =>
    apiPatch<PermissionGroupStateResponse, PermissionGroupStateResponse["state"]>(
      "/auth/permissions/groups",
      compactPermissionGroupState(state)
    ),
  objectScope: () => apiGet<ObjectScopeStateResponse>("/auth/permissions/object-scope"),
  updateObjectScope: (state: ObjectScopeStateResponse["state"]) =>
    apiPatch<ObjectScopeStateResponse, ObjectScopeStateResponse["state"]>("/auth/permissions/object-scope", state),
  createAdminUser: (payload: { username: string; email: string; firstName: string; lastName: string; phone?: string; displayName?: string; groupId?: string; isPlatformAdmin?: boolean; password: string }) =>
    apiPost<AdminUserRow, { username: string; email: string; firstName: string; lastName: string; phone?: string; displayName?: string; groupId?: string; isPlatformAdmin?: boolean; password: string }>("/auth/users", payload),
  updateAdminUser: (id: string, payload: Partial<{ email: string; firstName: string; lastName: string; phone?: string; displayName: string; groupId: string | null; isPlatformAdmin: boolean; status: "aktiv" | "inaktiv"; password: string }>) =>
    apiPatch<AdminUserRow, Partial<{ email: string; firstName: string; lastName: string; phone?: string; displayName: string; groupId: string | null; isPlatformAdmin: boolean; status: "aktiv" | "inaktiv"; password: string }>>(`/auth/users/${id}`, payload),
  uploadAdminUserAvatar: (id: string, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return apiPostForm<AdminUserRow>(`/auth/users/${id}/avatar`, formData);
  },
  deleteAdminUserAvatar: (id: string) => apiDelete(`/auth/users/${id}/avatar`),
  deleteAdminUser: (id: string) => apiDelete(`/auth/users/${id}`),
  tenants: () => apiGet<{ data: TenantRow[] }>("/auth/tenants"),
  createTenant: (payload: {
    slug: string;
    name: string;
    admin: { username: string; displayName: string; password: string };
    onboarding: {
      legalForm?: string;
      website?: string;
      contactEmail: string;
      contactPhone?: string;
      address?: { street?: string; postalCode?: string; city?: string; country?: string };
      primaryContact?: {
        salutation?: string;
        title?: string;
        firstName?: string;
        lastName?: string;
        email?: string;
        phone?: string;
      };
      technical?: { timezone?: string; locale?: string; currency?: string; uiFramework?: "classic" | "mockup_v2" };
      defaults?: {
        faciliooEnabled?: boolean;
        stimmrechtsart?: "kopf" | "stimmrecht" | "mea";
        quorum?: "allstimmig" | "einfache_mehrheit" | "qualifizierte_mehrheit";
      };
      trialDays?: number;
    };
  }) =>
    apiPost<
      TenantCreateResult,
      {
        slug: string;
        name: string;
        admin: { username: string; displayName: string; password: string };
        onboarding: {
          legalForm?: string;
          website?: string;
          contactEmail: string;
          contactPhone?: string;
          address?: { street?: string; postalCode?: string; city?: string; country?: string };
          primaryContact?: {
            salutation?: string;
            title?: string;
            firstName?: string;
            lastName?: string;
            email?: string;
            phone?: string;
          };
          technical?: { timezone?: string; locale?: string; currency?: string; uiFramework?: "classic" | "mockup_v2" };
          defaults?: {
            faciliooEnabled?: boolean;
            stimmrechtsart?: "kopf" | "stimmrecht" | "mea";
            quorum?: "allstimmig" | "einfache_mehrheit" | "qualifizierte_mehrheit";
          };
          trialDays?: number;
        };
      }
    >(
      "/auth/tenants",
      payload
    ),
  updateTenant: (
    slug: string,
    payload: {
      name?: string;
      status?: "aktiv" | "inaktiv";
      admin?: {
        username?: string;
        displayName?: string;
        password?: string;
      };
      onboarding?: {
        legalForm?: string;
        website?: string;
        contactEmail?: string;
        contactPhone?: string;
        address?: { street?: string; postalCode?: string; city?: string; country?: string };
        primaryContact?: {
          salutation?: string;
          title?: string;
          firstName?: string;
          lastName?: string;
          email?: string;
          phone?: string;
        };
        technical?: { timezone?: string; locale?: string; currency?: string; uiFramework?: "classic" | "mockup_v2" };
        defaults?: {
          faciliooEnabled?: boolean;
          stimmrechtsart?: "kopf" | "stimmrecht" | "mea";
          quorum?: "allstimmig" | "einfache_mehrheit" | "qualifizierte_mehrheit";
        };
        trialDays?: number | null;
      };
    }
  ) =>
    apiPatch<
      TenantUpdateResult,
      {
        name?: string;
        status?: "aktiv" | "inaktiv";
        admin?: {
          username?: string;
          displayName?: string;
          password?: string;
        };
        onboarding?: {
          legalForm?: string;
          website?: string;
          contactEmail?: string;
          contactPhone?: string;
          address?: { street?: string; postalCode?: string; city?: string; country?: string };
          primaryContact?: {
            salutation?: string;
            title?: string;
            firstName?: string;
            lastName?: string;
            email?: string;
            phone?: string;
          };
          technical?: { timezone?: string; locale?: string; currency?: string; uiFramework?: "classic" | "mockup_v2" };
          defaults?: {
            faciliooEnabled?: boolean;
            stimmrechtsart?: "kopf" | "stimmrecht" | "mea";
            quorum?: "allstimmig" | "einfache_mehrheit" | "qualifizierte_mehrheit";
          };
          trialDays?: number | null;
        };
      }
    >(`/auth/tenants/${encodeURIComponent(slug)}`, payload),
  retryTenantProvisioning: (slug: string) =>
    apiPost<TenantProvisioningRetryResult, Record<string, never>>(
      `/auth/tenants/${encodeURIComponent(slug)}/provisioning/retry`,
      {}
    ),
  deleteTenant: (slug: string) => apiDelete(`/auth/tenants/${encodeURIComponent(slug)}`),
  getTenantBilling: (slug: string) =>
    apiGet<TenantBillingStatus>(`/auth/tenants/${encodeURIComponent(slug)}/billing`),
  createTenantBillingCheckout: (slug: string) =>
    apiPost<TenantBillingCheckoutResult, Record<string, never>>(
      `/auth/tenants/${encodeURIComponent(slug)}/billing/checkout`,
      {}
    ),
  createTenantBillingPortal: (slug: string) =>
    apiPost<TenantBillingPortalResult, Record<string, never>>(
      `/auth/tenants/${encodeURIComponent(slug)}/billing/portal`,
      {}
    ),
  syncTenantBilling: (slug: string) =>
    apiPost<TenantBillingSyncResult, Record<string, never>>(
      `/auth/tenants/${encodeURIComponent(slug)}/billing/sync`,
      {}
    ),
  getTenantFaciliooDebug: (slug: string) =>
    apiGet<TenantFaciliooDebugState>(`/auth/tenants/${encodeURIComponent(slug)}/facilioo-debug`),
  updateTenantFaciliooDebug: (slug: string, payload: { debugModeEnabled: boolean; deepProbeEnabled: boolean }) =>
    apiPut<TenantFaciliooDebugState, { debugModeEnabled: boolean; deepProbeEnabled: boolean }>(
      `/auth/tenants/${encodeURIComponent(slug)}/facilioo-debug`,
      payload
    ),

  objects: (query: Query) => apiGet<ListResult<ObjectRow>>("/objects", query),
  objectById: (id: string) => apiGet<ObjectSummary["object"]>(`/objects/${id}`),
  objectSummary: (id: string) => apiGet<ObjectSummary>(`/objects/${id}/summary`),
  units: (query: Query) => apiGet<ListResult<UnitRow>>("/units", query),
  owners: async (query: Query) => {
    const raw = await apiGet<ListResult<Record<string, unknown>>>("/owners", query);
    return {
      ...raw,
      data: raw.data.map((r) => ({
        id: String(r.id),
        objectId: String(r.objectId ?? ""),
        objektnummer: String(r.objektnummer ?? ""),
        objectRefs: Array.isArray(r.objectRefs)
          ? r.objectRefs
              .map((entry) => ({
                id: String((entry as { id?: unknown }).id ?? ""),
                objektnummer: String((entry as { objektnummer?: unknown }).objektnummer ?? ""),
                status: ((entry as { status?: unknown }).status ? String((entry as { status?: unknown }).status) : undefined) as
                  | "aktiv"
                  | "inaktiv"
                  | "keine_zuordnung"
                  | undefined,
                assignmentDetails: Array.isArray((entry as { assignmentDetails?: unknown }).assignmentDetails)
                  ? ((entry as { assignmentDetails?: unknown[] }).assignmentDetails ?? []).map((detail) => ({
                      objektnummer: String((detail as { objektnummer?: unknown }).objektnummer ?? ""),
                      einheitsnummer: String((detail as { einheitsnummer?: unknown }).einheitsnummer ?? ""),
                      gueltigAb: (detail as { gueltigAb?: unknown }).gueltigAb
                        ? String((detail as { gueltigAb?: unknown }).gueltigAb)
                        : undefined,
                      gueltigBis: (detail as { gueltigBis?: unknown }).gueltigBis
                        ? String((detail as { gueltigBis?: unknown }).gueltigBis)
                        : undefined,
                      status: ((detail as { status?: unknown }).status ? String((detail as { status?: unknown }).status) : undefined) as
                        | "aktiv"
                        | "inaktiv"
                        | undefined
                    }))
                  : undefined
              }))
              .filter((entry) => !!entry.id && !!entry.objektnummer)
          : undefined,
        eigentuemer_id: String(r.eigentuemer_id ?? ""),
        anrede: r.anrede ? String(r.anrede) : undefined,
        titel: r.titel ? String(r.titel) : undefined,
        vorname: r.vorname ? String(r.vorname) : undefined,
        name_1: r.name_1 ? String(r.name_1) : undefined,
        name_2: r.name_2 ? String(r.name_2) : undefined,
        name_3: r.name_3 ? String(r.name_3) : undefined,
        briefanrede_1: r.briefanrede_1 ? String(r.briefanrede_1) : undefined,
        briefanrede_2: r.briefanrede_2 ? String(r.briefanrede_2) : undefined,
        strasse: r.strasse ? String(r.strasse) : undefined,
        plz: r.plz ? String(r.plz) : undefined,
        ort: r.ort ? String(r.ort) : undefined,
        land_code: r.land_code ? String(r.land_code) : undefined,
        land: r.land ? String(r.land) : undefined,
        vollname: String(r.vollname ?? [r.vorname ?? "", [r.name_1 ?? "", r.name_2 ?? "", r.name_3 ?? ""].filter(Boolean).join(" | ")].filter(Boolean).join(" ").trim()),
        email: r.email ? String(r.email) : undefined,
        telefon: r.telefon ? String(r.telefon) : undefined,
        ist_analog: Boolean(r.ist_analog ?? !(r.email && String(r.email).trim())),
        erreichbarkeit: (Boolean(r.ist_analog ?? !(r.email && String(r.email).trim())) ? "analog" : "digital") as "digital" | "analog",
        anzahl_einheiten: Number(r.anzahl_einheiten ?? 0),
        gueltig_ab: r.gueltig_ab ? String(r.gueltig_ab) : undefined,
        gueltig_bis: r.gueltig_bis ? String(r.gueltig_bis) : undefined,
        status: String(r.status ?? "aktiv") as "aktiv" | "inaktiv" | "keine_zuordnung",
        letzte_aenderung: String(r.letzte_aenderung ?? r.updatedAt ?? "")
      }))
    } as ListResult<OwnerRow>;
  },
  assignments: async (query: Query) => {
    const raw = await apiGet<ListResult<Record<string, unknown>>>("/assignments", query);
    return {
      ...raw,
      data: raw.data.map((r) => ({
        id: String(r.id),
        objectId: String(r.objectId ?? ""),
        unitId: String(r.unitId ?? ""),
        ownerId: String(r.ownerId ?? ""),
        objektnummer: String(r.objektnummer ?? ""),
        einheitsnummer: String(r.einheitsnummer ?? ""),
        eigentuemer_id: String(r.eigentuemer_id ?? ""),
        eigentuemername: String(r.eigentuemername ?? ""),
        eigentuemer_email: r.eigentuemer_email ? String(r.eigentuemer_email) : "",
        einheit_stimmrecht: Number(r.einheit_stimmrecht ?? 0),
        einheit_mea: r.einheit_mea !== undefined ? Number(r.einheit_mea) : undefined,
        gueltig_ab: r.gueltig_ab ? String(r.gueltig_ab) : undefined,
        gueltig_bis: r.gueltig_bis ? String(r.gueltig_bis) : undefined,
        contractId: r.contractId ? String(r.contractId) : r.contract_id ? String(r.contract_id) : undefined,
        contractTypeId: Number.isFinite(Number(r.contractTypeId)) ? Number(r.contractTypeId) : null,
        isAuthorizedOwnerRecipient: Boolean(r.isAuthorizedOwnerRecipient),
        rolle: r.rolle ? String(r.rolle) : null,
        letzte_aenderung: r.letzte_aenderung ? String(r.letzte_aenderung) : undefined,
        status: String(r.status ?? "aktiv") as "aktiv" | "inaktiv"
      }))
    } as ListResult<AssignmentRow>;
  },
  resolutions: (query: Query) => apiGet<ListResult<ResolutionRow>>("/resolutions", query),
  nextResolutionNumber: () => apiGet<NextResolutionNumberResponse>("/resolutions/next-beschlussnummer"),
  resolutionById: (id: string) => apiGet<ResolutionRow>(`/resolutions/${id}`),
  resolutionRecipients: (id: string, query?: Query) =>
    apiGet<ListResult<ResolutionRecipientRow>>(`/resolutions/${id}/recipients`, query),
  resolutionVotes: (id: string) => apiGet<{ data: ResolutionVoteRow[] }>(`/resolutions/${id}/votes`),
  resolutionOwnerAccesses: (id: string) => apiGet<{ data: OwnerVoteAccessRow[] }>(`/resolutions/${id}/owner-accesses`),
  resolutionVoteLog: (id: string) => apiGet<{ data: ResolutionVoteLogRow[] }>(`/resolutions/${id}/vote-log`),
  resolutionDispatchLog: (id: string, query?: Query) =>
    apiGet<{ data: ResolutionDispatchLogRow[] }>(`/resolutions/${id}/dispatch-log`, query),
  validateFaciliooProcessForObject: (objectId: string, processRef: string) =>
    apiPost<FaciliooProcessValidationResult, { objectId: string; processRef: string }>(
      "/resolutions/facilioo/validate-process",
      { objectId, processRef }
    ),
  resolutionTriggerNotificationsLog: (id: string) =>
    apiGet<{ data: ResolutionTriggerNotificationLogRow[] }>(`/resolutions/${id}/trigger-notifications/log`),
  resolutionDemoDispatchStatus: (id: string, limit = 120) =>
    apiGet<DemoDispatchStatusResponse>(`/resolutions/${id}/dispatch-demo/status`, { limit }),
  startResolutionDemoDispatch: (
    id: string,
    payload: { eventType: DemoDispatchEventType; maxPerSecond?: number; failRatePercent?: number }
  ) => apiPost<{ job: DemoDispatchJobSummary & { messages: DemoDispatchMessageRow[] } }, { eventType: DemoDispatchEventType; maxPerSecond?: number; failRatePercent?: number }>(`/resolutions/${id}/dispatch-demo/start`, payload),
  cancelResolutionDemoDispatch: (id: string, jobId: string) =>
    apiPost<{ job: DemoDispatchJobSummary }, Record<string, never>>(`/resolutions/${id}/dispatch-demo/jobs/${jobId}/cancel`, {}),
  resolutionResult: (id: string) => apiGet<ResolutionResult>(`/resolutions/${id}/result`),
  resolutionKpis: (id: string) => apiGet<ResolutionKpiResult>(`/resolutions/${id}/kpis`),
  subscribeResolutionEvents: (
    id: string,
    options: {
      onEvent?: (event: string, payload: unknown) => void;
      onError?: (error: Error) => void;
      onClose?: () => void;
    } = {}
  ) => {
    const controller = new AbortController();
    let closed = false;

    const start = async () => {
      try {
        const res = await fetch(`${API_BASE}/resolutions/${id}/events-stream`, {
          method: "GET",
          headers: {
            ...(buildHeaders(false) as Record<string, string>),
            Accept: "text/event-stream"
          },
          signal: controller.signal
        });
        if (!res.ok) throw new Error(await buildApiErrorMessage(res));
        if (!res.body) {
          options.onClose?.();
          return;
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";
        while (!closed) {
          const chunk = await reader.read();
          if (chunk.done) break;
          buffer += decoder.decode(chunk.value, { stream: true });
          const blocks = buffer.split(/\r?\n\r?\n/);
          buffer = blocks.pop() ?? "";
          for (const block of blocks) {
            const parsed = parseSseBlock(block);
            if (!parsed) continue;
            options.onEvent?.(parsed.event, parsed.data);
          }
        }
        options.onClose?.();
      } catch (error) {
        if (!controller.signal.aborted) {
          options.onError?.(error instanceof Error ? error : new Error("SSE-Verbindung fehlgeschlagen."));
        }
      }
    };

    void start();
    return {
      close: () => {
        if (closed) return;
        closed = true;
        controller.abort();
        options.onClose?.();
      }
    };
  },
  resolutionDocuments: async (id: string) => {
    const res = await apiGet<{ data: ResolutionDocumentRow[] }>(`/resolutions/${id}/documents`);
    return { ...res, data: (res.data ?? []).map((row) => ({ ...row, fileUrl: toApiFileUrl(row.fileUrl) })) };
  },
  resolutionFaciliooAttachments: (id: string) =>
    apiGet<{ data: ResolutionFaciliooAttachmentRow[] }>(`/resolutions/${id}/facilioo-attachments`),
  resolutionFaciliooFileTypesDebug: (id: string) =>
    apiGet<{ data: ResolutionFaciliooFileTypeDebugRow[]; total: number }>(`/resolutions/${id}/facilioo-file-types-debug`),
  fetchResolutionFaciliooAttachmentBlob: (
    id: string,
    feedId: string,
    attachmentId: string,
    options?: { fileName?: string; fileExtension?: string; mimeType?: string; download?: boolean }
  ) => {
    const params = new URLSearchParams();
    if (options?.fileName) params.set("name", options.fileName);
    if (options?.fileExtension) params.set("ext", options.fileExtension);
    if (options?.mimeType) params.set("mime", options.mimeType);
    if (options?.download) params.set("download", "1");
    const query = params.toString();
    const path = `/resolutions/${encodeURIComponent(id)}/facilioo-attachments/${encodeURIComponent(feedId)}/${encodeURIComponent(attachmentId)}/file${query ? `?${query}` : ""}`;
    return apiGetBlob(path);
  },
  importResolutionFaciliooAttachments: (id: string, items: Array<{ feedId: string; attachmentId: string; originalFilename?: string }>) =>
    apiPost<{ data: ResolutionDocumentRow[]; imported: number; skipped?: Array<{ feedId: string; attachmentId: string; reason: string }> }, { items: Array<{ feedId: string; attachmentId: string; originalFilename?: string }> }>(
      `/resolutions/${id}/facilioo-attachments/import`,
      { items }
    ),
  uploadResolutionDocuments: (id: string, files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    return apiPostForm<{ data: ResolutionDocumentRow[] }>(`/resolutions/${id}/documents`, formData);
  },
  uploadResolutionDocumentsToFacilioo: (id: string, documentIds: string[]) =>
    apiPost<ResolutionFaciliooUploadDocumentsResult, { documentIds: string[] }>(
      `/resolutions/${id}/documents/facilioo-upload`,
      { documentIds }
    ),
  deleteResolutionDocument: (id: string, documentId: string) => apiDelete(`/resolutions/${id}/documents/${documentId}`),
  imports: (query: Query) => apiGet<ListResult<ImportJobRow>>("/imports", query),
  importById: (jobId: string) => apiGet<ImportJobRow>(`/imports/${jobId}`),
  importErrors: (jobId: string) => apiGet<{ data: ImportErrorRow[] }>(`/imports/${jobId}/errors`),
  validateImport: (jobId: string, mapping: Record<string, string>) =>
    apiPost<ImportValidateResult, { mapping: Record<string, string> }>(`/imports/${jobId}/validate`, { mapping }),
  commitImport: (jobId: string, confirmedErrorIds: string[] = []) =>
    apiPost<{ message: string; importedRows: number }, { confirmedErrorIds: string[] }>(`/imports/${jobId}/commit`, { confirmedErrorIds }),
  uploadImportFile: (importType: ImportType, file: File) => {
    const formData = new FormData();
    formData.append("importType", importType);
    formData.append("file", file);
    return apiPostForm<ImportJobRow>("/imports/upload", formData);
  },
  uploadImportFromFacilioo: (importType: ImportType) =>
    apiPost<FaciliooUploadResult, { importType: ImportType }>("/imports/facilioo/upload", { importType }),
  downloadImportTemplate: async (importType: ImportType, format: "csv" | "xlsx" = "csv") => {
    const blob = await apiGetBlob(`/imports/templates/${importType}?format=${format}`);
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${importType}.${format}`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  },
  auditLogs: (query: Query) => apiGet<ListResult<AuditRow>>("/audit-logs", query),
  faciliooSyncLogs: (query: Query) => apiGet<ListResult<AuditRow>>("/audit-logs/facilioo-sync", query),
  faciliooWebhookLogs: (query: Query) => apiGet<ListResult<FaciliooWebhookQueueRow>>("/audit-logs/facilioo-webhooks", query),
  faciliooWebhookRejectLogs: (query: Query) => apiGet<ListResult<FaciliooWebhookRejectRow>>("/audit-logs/facilioo-webhooks/rejects", query),
  voteProtocols: (query: VoteProtocolsQuery) =>
    apiGet<ListResult<VoteProtocolRow>>("/audit-logs/votes", query),
  createObject: (payload: CreateObjectInput) => apiPost<ObjectRow, CreateObjectInput>("/objects", payload),
  createUnit: (payload: CreateUnitInput) => apiPost<UnitRow, CreateUnitInput>("/units", payload),
  createOwner: (payload: CreateOwnerInput) => apiPost<OwnerRow, CreateOwnerInput>("/owners", payload),
  createAssignment: (payload: CreateAssignmentInput) => apiPost<AssignmentRow, CreateAssignmentInput>("/assignments", payload),
  createResolution: (payload: CreateResolutionInput) => apiPost<ResolutionRow, CreateResolutionInput>("/resolutions", payload),
  updateObject: (id: string, payload: Partial<CreateObjectInput>) => apiPatch<ObjectRow, Partial<CreateObjectInput>>(`/objects/${id}`, payload),
  updateUnit: (id: string, payload: Partial<Omit<CreateUnitInput, "objectId">>) => apiPatch<UnitRow, Partial<Omit<CreateUnitInput, "objectId">>>(`/units/${id}`, payload),
  updateOwner: (id: string, payload: Partial<CreateOwnerInput>) => apiPatch<OwnerRow, Partial<CreateOwnerInput>>(`/owners/${id}`, payload),
  updateAssignment: (id: string, payload: CreateAssignmentInput) =>
    apiPatch<AssignmentRow, CreateAssignmentInput>(`/assignments/${id}`, payload),
  updateResolution: (id: string, payload: Partial<CreateResolutionInput>) =>
    apiPatch<ResolutionRow, Partial<CreateResolutionInput>>(`/resolutions/${id}`, payload),
  startResolution: (id: string, payload?: { expectedUpdatedAt?: string; idempotency_key?: string }) =>
    apiPost<ResolutionRow, { expectedUpdatedAt?: string; idempotency_key?: string }>(`/resolutions/${id}/start`, payload ?? {}),
  stopResolution: (id: string, payload?: { expectedUpdatedAt?: string; idempotency_key?: string }) =>
    apiPost<ResolutionRow, { expectedUpdatedAt?: string; idempotency_key?: string }>(`/resolutions/${id}/stop`, payload ?? {}),
  deleteResolution: (id: string) => apiDelete(`/resolutions/${id}`),
  upsertResolutionVote: (id: string, payload: CreateResolutionVoteInput) =>
    apiPost<ResolutionVoteRow, CreateResolutionVoteInput>(`/resolutions/${id}/votes`, payload),
  ownerVotingLogin: (payload: OwnerVotingLoginInput) => apiPost<OwnerVotingLoginResult, OwnerVotingLoginInput>(`/resolutions/owner-voting/login`, payload),
  ownerVotingAccess: (token: string) => apiGet<OwnerVotingAccessData>(`/resolutions/owner-voting/access/${token}`),
  ownerVotingResult: (token: string) => apiGet<ResolutionResult>(`/resolutions/owner-voting/access/${token}/result`),
  ownerVotingDocuments: async (token: string) => {
    const res = await apiGet<{ data: ResolutionDocumentRow[] }>(`/resolutions/owner-voting/access/${token}/documents`);
    return { ...res, data: (res.data ?? []).map((row) => ({ ...row, fileUrl: toApiFileUrl(row.fileUrl) })) };
  },
  ownerVotingSubmit: (token: string, payload: OwnerVotingSubmitInput) =>
    apiPost<{ status: string; applied_count: number; voteValue: string; voteReceivedAt: string }, OwnerVotingSubmitInput>(`/resolutions/owner-voting/access/${token}/vote`, payload),
  analogScanAccess: (token: string) => apiGet<AnalogScanAccessData>(`/resolutions/analog-scan/${token}`),
  analogScanSubmit: (token: string, payload: AnalogScanSubmitInput) =>
    apiPost<{ status: string; applied_count: number; voteValue: string; voteReceivedAt: string }, AnalogScanSubmitInput>(
      `/resolutions/analog-scan/${token}/vote`,
      payload
    ),
  deactivateObject: (id: string) => apiPatch<ObjectRow, Record<string, never>>(`/objects/${id}/deactivate`, {}),
  deactivateUnit: (id: string) => apiPatch<UnitRow, Record<string, never>>(`/units/${id}/deactivate`, {}),
  deactivateOwner: (id: string) => apiPatch<OwnerRow, Record<string, never>>(`/owners/${id}/deactivate`, {}),
  deleteAssignment: (id: string) => apiDelete(`/assignments/${id}`),
  documentTemplates: async () => {
    const res = await apiGet<DocumentTemplatesResponse>("/document-templates");
    return {
      ...res,
      letterheads: {
        digital: res.letterheads?.digital
          ? { ...res.letterheads.digital, fileUrl: res.letterheads.digital.fileUrl ? toApiFileUrl(res.letterheads.digital.fileUrl) : undefined }
          : undefined,
        analog: res.letterheads?.analog
          ? { ...res.letterheads.analog, fileUrl: res.letterheads.analog.fileUrl ? toApiFileUrl(res.letterheads.analog.fileUrl) : undefined }
          : undefined,
        digital_followup: res.letterheads?.digital_followup
          ? {
              ...res.letterheads.digital_followup,
              fileUrl: res.letterheads.digital_followup.fileUrl ? toApiFileUrl(res.letterheads.digital_followup.fileUrl) : undefined
            }
          : undefined,
        analog_followup: res.letterheads?.analog_followup
          ? {
              ...res.letterheads.analog_followup,
              fileUrl: res.letterheads.analog_followup.fileUrl ? toApiFileUrl(res.letterheads.analog_followup.fileUrl) : undefined
            }
          : undefined
      }
    };
  },
  updateDocumentTemplate: (key: DocumentTemplateKey, payload: { subject: string; body: string; expectedUpdatedAt?: string }) =>
    apiPut<DocumentTemplatesResponse, { subject: string; body: string; expectedUpdatedAt?: string }>(`/document-templates/${key}`, payload),
  previewDocumentTemplate: (payload: { resolutionId: string; templateKey: DocumentTemplateKey; ownerId?: string }) =>
    apiPost<DocumentTemplatePreviewResponse, { resolutionId: string; templateKey: DocumentTemplateKey; ownerId?: string }>("/document-templates/preview", payload),
  previewDocumentTemplateMail: (payload: {
    templateKey: DocumentTemplateKey;
    resolutionId?: string;
    ownerId?: string;
    eventType?: DocumentTemplateEventType;
  }) =>
    apiPost<DocumentTemplateMailPreviewResponse, {
      templateKey: DocumentTemplateKey;
      resolutionId?: string;
      ownerId?: string;
      eventType?: DocumentTemplateEventType;
    }>("/document-templates/preview-mail", payload),
  previewDocumentTemplatePdf: (payload: {
    templateKey: DocumentTemplateKey;
    resolutionId?: string;
    ownerId?: string;
    eventType?: DocumentTemplateEventType;
  }) =>
    apiPostBlob<{
      templateKey: DocumentTemplateKey;
      resolutionId?: string;
      ownerId?: string;
      eventType?: DocumentTemplateEventType;
    }>("/document-templates/preview-pdf", payload),
  sendDocumentTemplatePreviewMailTest: (payload: {
    templateKey: DocumentTemplateKey;
    recipientEmail: string;
    resolutionId?: string;
    ownerId?: string;
    eventType?: DocumentTemplateEventType;
  }) =>
    apiPost<DocumentTemplateMailSendTestResponse, {
      templateKey: DocumentTemplateKey;
      recipientEmail: string;
      resolutionId?: string;
      ownerId?: string;
      eventType?: DocumentTemplateEventType;
    }>("/document-templates/preview-mail/send-test", payload),
  generateDispatchDocuments: async (payload: { resolutionId: string; eventType: "einladung_versendet" | "erinnerung_1_versendet" | "erinnerung_2_versendet" | "beschlussergebnis_versendet"; sendDigital?: boolean }) => {
    const res = await apiPost<DispatchGenerateResult, { resolutionId: string; eventType: "einladung_versendet" | "erinnerung_1_versendet" | "erinnerung_2_versendet" | "beschlussergebnis_versendet"; sendDigital?: boolean }>("/document-templates/generate", payload);
    return { ...res, data: (res.data ?? []).map((row) => ({ ...row, fileUrl: toApiFileUrl(row.fileUrl) })) };
  },
  generatedDispatchDocuments: async (resolutionId: string) => {
    const res = await apiGet<{ data: GeneratedDispatchRow[] }>("/document-templates/generated", { resolutionId });
    return { ...res, data: (res.data ?? []).map((row) => ({ ...row, fileUrl: toApiFileUrl(row.fileUrl) })) };
  },
  downloadGeneratedDispatchZip: (
    resolutionId: string,
    eventType: "einladung_versendet" | "erinnerung_1_versendet" | "erinnerung_2_versendet" | "beschlussergebnis_versendet"
  ) => {
    const params = new URLSearchParams({ resolutionId, eventType });
    return apiGetBlob(`/document-templates/generated/zip?${params.toString()}`);
  },
  retryGeneratedDispatchMail: (
    id: string,
    payload?: {
      resolutionId?: string;
      eventType?: "einladung_versendet" | "erinnerung_1_versendet" | "erinnerung_2_versendet" | "beschlussergebnis_versendet";
      useCurrentOwnerEmail?: boolean;
    }
  ) =>
    apiPost<RetryGeneratedMailResult, {
      resolutionId?: string;
      eventType?: "einladung_versendet" | "erinnerung_1_versendet" | "erinnerung_2_versendet" | "beschlussergebnis_versendet";
      useCurrentOwnerEmail?: boolean;
    }>(
      `/document-templates/generated/${id}/retry-mail`,
      payload ?? {}
    ),
  retryFailedGeneratedDispatchMails: (
    payload: {
      resolutionId?: string;
      eventType?: "einladung_versendet" | "erinnerung_1_versendet" | "erinnerung_2_versendet" | "beschlussergebnis_versendet";
      limit?: number;
      maxAttempts?: number;
      baseDelayMs?: number;
      maxDelayMs?: number;
    }
  ) =>
    apiPost<RetryFailedGeneratedMailsResult, {
      resolutionId?: string;
      eventType?: "einladung_versendet" | "erinnerung_1_versendet" | "erinnerung_2_versendet" | "beschlussergebnis_versendet";
      limit?: number;
      maxAttempts?: number;
      baseDelayMs?: number;
      maxDelayMs?: number;
    }>(
      "/document-templates/generated/retry-failed",
      payload
    ),
  uploadDocumentLetterhead: async (kanal: "digital" | "analog" | "digital_followup" | "analog_followup", file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await apiPostForm<{ kanal: "digital" | "analog" | "digital_followup" | "analog_followup"; fileName: string; fileUrl: string; updatedAt: string }>(
      `/document-templates/letterheads/${kanal}`,
      formData
    );
    return { ...res, fileUrl: toApiFileUrl(res.fileUrl) };
  },
  deleteDocumentLetterhead: (kanal: "digital" | "analog" | "digital_followup" | "analog_followup") => apiDelete(`/document-templates/letterheads/${kanal}`),
  updateDocumentLetterheadMargins: (
    kanal: "digital" | "analog" | "digital_followup" | "analog_followup",
    payload: { topMarginMm: number; bottomMarginMm: number }
  ) =>
    apiPatch<
      { kanal: "digital" | "analog" | "digital_followup" | "analog_followup"; fileName?: string; fileUrl?: string; updatedAt?: string; topMarginMm: number; bottomMarginMm: number },
      { topMarginMm: number; bottomMarginMm: number }
    >(`/document-templates/letterheads/${kanal}/margins`, payload),
  updateDocumentLetterheadFirstPageLayout: (
    kanal: "digital" | "analog",
    payload: { firstPageRecipientTopMm: number; firstPageBodyStartMm: number }
  ) =>
    apiPatch<
      {
        kanal: "digital" | "analog";
        fileName?: string;
        fileUrl?: string;
        updatedAt?: string;
        topMarginMm: number;
        bottomMarginMm: number;
        firstPageRecipientTopMm: number;
        firstPageBodyStartMm: number;
      },
      { firstPageRecipientTopMm: number; firstPageBodyStartMm: number }
    >(`/document-templates/letterheads/${kanal}/first-page-layout`, payload),
  uiSettings: () => apiGet<UiSettingsApi>("/settings"),
  uiBranding: () => apiGet<UiBrandingApi>("/settings/public-branding"),
  getDemoRequestConfig: () => apiGet<DemoRequestConfig>("/public/demo-request/config"),
  submitDemoRequest: (payload: DemoRequestPayload) =>
    apiPost<{ ok: boolean }, DemoRequestPayload>("/public/demo-request", payload),
  saveUiSettings: (payload: UiSettingsApi, options?: { clearDemoDataOnDisable?: boolean }) =>
    apiPut<UiSettingsApi, UiSettingsApi>(
      options?.clearDemoDataOnDisable ? "/settings?clearDemoDataOnDisable=1" : "/settings",
      payload
    ),
  disableAndClearDemoData: () => apiPost<UiSettingsApi, Record<string, never>>("/settings/demo-data/disable-and-clear", {}),
  clearDataByType: (type: DataInventoryType) =>
    apiPost<DataClearResult, { type: DataInventoryType }>("/settings/data/clear", { type }),
  testMailSettings: (
    recipientEmail: string,
    mail?: UiSettingsApi["mail"]
  ) =>
    apiPost<MailTestResult, { recipientEmail: string; mail?: UiSettingsApi["mail"] }>("/settings/mail/test", {
      recipientEmail,
      mail
    }),
  getMicrosoftMailStatus: () => apiGet<MicrosoftMailStatus>("/settings/mail/microsoft/status"),
  getMicrosoftMailFolders: (mailbox?: string) => {
    const params = new URLSearchParams();
    const mailboxValue = String(mailbox ?? "").trim();
    if (mailboxValue) params.set("mailbox", mailboxValue);
    const query = params.toString();
    return apiGet<MicrosoftMailFoldersResult>(`/settings/mail/microsoft/folders${query ? `?${query}` : ""}`);
  },
  connectMicrosoftMail: (returnPath: string) =>
    apiPost<{ ok: boolean; authUrl: string }, { returnPath: string }>("/settings/mail/microsoft/connect", { returnPath }),
  disconnectMicrosoftMail: () =>
    apiPost<{ ok: boolean; connected: boolean }, Record<string, never>>("/settings/mail/microsoft/disconnect", {}),
  testFaciliooConnection: () => apiPost<FaciliooTestResult, Record<string, never>>("/settings/facilioo/test", {}),
  getFaciliooDocumentGroups: () => apiGet<FaciliooDocumentGroupsResult>("/settings/facilioo/document-groups"),
  ensureFaciliooDocumentGroup: (payload?: FaciliooEnsureDocumentGroupPayload) =>
    apiPost<FaciliooEnsureDocumentGroupResult, FaciliooEnsureDocumentGroupPayload>("/settings/facilioo/document-groups/ensure", payload ?? {}),
  resetFaciliooDocumentGroup: () =>
    apiPost<FaciliooResetDocumentGroupResult, Record<string, never>>("/settings/facilioo/document-groups/reset", {}),
  getFaciliooWebhookTarget: () => apiGet<FaciliooWebhookTargetStatus>("/settings/facilioo/webhook-target"),
  regenerateFaciliooWebhookTargetKey: (name = "default") =>
    apiPost<FaciliooWebhookTargetRegenerateResult, { name: string }>("/settings/facilioo/webhook-target/regenerate", { name }),
  registerFaciliooWebhookTarget: (payload?: {
    replaceExisting?: boolean;
    shouldGroup?: boolean;
    shouldGroupContinueAfterFail?: boolean;
    includePropertyCreated?: boolean;
    enabledEvents?: Array<{ type: number; entityName: string }>;
  }) =>
    apiPost<
      FaciliooWebhookTargetRegisterResult,
      {
        replaceExisting?: boolean;
        shouldGroup?: boolean;
        shouldGroupContinueAfterFail?: boolean;
        includePropertyCreated?: boolean;
        enabledEvents?: Array<{ type: number; entityName: string }>;
      }
    >("/settings/facilioo/webhook-target/register", payload ?? {}),
  testFaciliooWebhookConnection: () =>
    apiPost<FaciliooWebhookConnectionTestResult, Record<string, never>>("/settings/facilioo/webhook-target/connection-test", {}),
  pingFaciliooWebhookEndpoint: () =>
    apiPost<FaciliooWebhookPingResult, Record<string, never>>("/settings/facilioo/webhook-target/ping", {}),
  probeFaciliooMasterData: () => apiGet<FaciliooMasterDataProbeResult>("/settings/facilioo/probe/master-data"),
  getFaciliooContractLive: (contractId: string) =>
    apiGet<FaciliooContractLiveResult>(`/settings/facilioo/contracts/${encodeURIComponent(contractId)}/live`),
  resolveFaciliooProcess: (processRef: string, options?: { postFeedEntry?: boolean; feedText?: string }) =>
    apiPost<FaciliooProcessResolveResult, { processRef: string; postFeedEntry?: boolean; feedText?: string }>(
      "/settings/facilioo/processes/resolve",
      {
        processRef,
        ...(options?.postFeedEntry ? { postFeedEntry: true } : {}),
        ...(options?.feedText?.trim() ? { feedText: options.feedText.trim() } : {})
      }
    ),
  getFaciliooSyncStatus: () => apiGet<FaciliooSyncStatus>("/settings/facilioo/sync/status"),
  getPlatformServicesStatus: (options?: { includeMemoryDebug?: boolean; historyMinutes?: number }) => {
    const params = new URLSearchParams();
    if (options?.includeMemoryDebug === false) params.set("includeMemoryDebug", "false");
    if (typeof options?.historyMinutes === "number" && Number.isFinite(options.historyMinutes) && options.historyMinutes > 0) {
      params.set("historyMinutes", String(Math.max(1, Math.floor(options.historyMinutes))));
    }
    const query = params.toString();
    return apiGet<PlatformServiceStatusResponse>(`/settings/services/status${query ? `?${query}` : ""}`);
  },
  checkPlatformServiceStatus: (service: PlatformServiceKey) =>
    apiPost<PlatformServiceCheckResponse, { service: PlatformServiceKey }>("/settings/services/check", { service }),
  notifyPlatformServiceStatus: (service: PlatformServiceKey) =>
    apiPost<PlatformServiceNotifyResponse, { service: PlatformServiceKey }>(
      "/settings/services/notify",
      { service }
    ),
  getTenantTrialStatus: () => apiGet<TenantTrialRuntime>("/settings/tenant-trial"),
  runFaciliooSyncNow: (
    objectNumber?: string,
    debugAccountId?: string,
    mode: "full" | "live_update" = "full"
  ) => {
    const payload: { objectNumber?: string; debugAccountId?: string; mode?: "full" | "live_update" } = {};
    if (objectNumber?.trim()) payload.objectNumber = objectNumber.trim();
    if (debugAccountId?.trim()) payload.debugAccountId = debugAccountId.trim();
    payload.mode = mode;
    return apiPost<
      { ok: boolean; running: boolean; message: string },
      { objectNumber?: string; debugAccountId?: string; mode?: "full" | "live_update" }
    >(
      "/settings/facilioo/sync/run",
      payload
    );
  },
  cancelFaciliooSync: () =>
    apiPost<{ ok: boolean; running: boolean; cancelRequested: boolean; message: string }, Record<string, never>>(
      "/settings/facilioo/sync/cancel",
      {}
    ),
  cleanupFaciliooMirrorUnits: (dryRun = false) =>
    apiPost<FaciliooMirrorCleanupResult, { dryRun: boolean }>("/settings/facilioo/units/cleanup-mirrors", { dryRun }),
  assignmentAnomalies: () => apiGet<AssignmentAnomaliesResult>("/dashboard/assignment-anomalies"),
  dashboardSummary: () => apiGet<DashboardSummary>("/dashboard/summary"),
  dashboard: async (): Promise<DashboardData> => {
    const [openRes, failedImports, recent] = await Promise.all([
      apiGet<ListResult<Record<string, unknown>>>("/resolutions", { page: 1, pageSize: 1, status: "aktiv" }),
      apiGet<ListResult<Record<string, unknown>>>("/imports", { page: 1, pageSize: 1, status: "failed" }),
      apiGet<ListResult<{ id: string; action: string; entity_type: string; entity_id: string; createdAt: string }>>("/audit-logs", {
        page: 1,
        pageSize: 8
      })
    ]);

    return {
      offeneBeschluesse: openRes.meta.total,
      ueberfaellig: 0,
      fehlerhafteImporte: failedImports.meta.total,
      unvollstaendigeObjekte: 0,
      recent: recent.data
    };
  }
};


































































